import { debounce } from 'lodash'
import * as React from 'react'
import { createPortal, unstable_batchedUpdates } from 'react-dom'
import { Engine, Node, NodeEditor } from 'rete'
import type { Input as ReteInput, Output as ReteOutput } from 'rete'
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import ConnectionPlugin from 'rete-connection-plugin'
import ContextMenuPlugin from 'rete-context-menu-plugin'
import MinimapPlugin from 'rete-minimap-plugin'
import ReactRenderPlugin from 'rete-react-render-plugin'
import { Data } from 'rete/types/core/data'
import { createDefaultComponents } from '../modelling/components'
import { applyProjectProperties, BaseComponent } from '../modelling/components/base_component'
import { TeamComponentData } from './project_editor'
import { isComponentPubliclyAvailable, usePublicShareSettings } from './public_share_settings'
import { SaveMapLayer } from '../modelling/components/map_layer_component'
import { NodeComponent } from './node_component'
import { SaveModel } from '../modelling/components/save_model_component'
import { getDatasets } from '../modelling/components/dataset_component'
import { getAnnotationsType } from '../modelling/components/paintbrush_component'
import { Extent } from 'ol/extent'
import { ProjectPermissions } from './project_editor'
import type { fillType } from '../state'
import { getExpressionsType } from '../modelling/components/expression_component'
import { AvailableExtent } from '../modelling/components/extents_component'
import { enableConnectionDeleteContextMenu } from '../../modelling/connection_context_menu'
import { PAYOFF_MATRIX_COMPONENT_NAME } from '../modelling/components/boolean_payoff_matrix_component'
import {
  CATEGORICAL_TO_NUMERIC_COMPONENT_NAME,
  mapValDataKeyForCategoryValue,
  persistCatMapValuesByLabelOnNode,
  syncCatMapValuesByLabelInReteModelData,
} from '../modelling/components/catergorical_to_numeric_component'
import {
  resumeCategoricalChainSeeding,
  suspendCategoricalChainSeeding,
} from '../modelling/components/categorical_dynamic_io_connections'
import { scheduleAllDynamicLabelOutputRefreshes } from '../modelling/components/categorical_label_chain'
import {
  NEW_TABLE_COMPONENT_NAME,
  stripTransientTableNodeData,
  TABLE_CELLS_KEY,
  TABLE_COLUMNS_KEY,
  TABLE_ROWS_KEY,
  type TableCell,
} from '../modelling/controls/table_editor'
import { mergeImportedTable, type TableSpreadsheetData } from '../modelling/table_spreadsheet'
import { parseOsmCustomQueryValue } from '../modelling/controls/osm_custom_query'
import { payoffCellKey } from '../modelling/payoff_matrix_logic'
import { CategoricalTileGrid } from '../modelling/tile_grid'
import type { ModelEditorApi } from './model_editor_api'
import { ModelAgentSidebar } from './model_view_sidebar'
import { autoLayoutNodes } from './model_view_layout'
import { setNodeFocusState } from './node_focus_store'
import { projectModelToReadable, componentsToReadable, mapLayerOutputsToReadable } from './project_model_readable'
import type { AgentActions, PublicExtentSwitchApi } from '../aegis/types'
import { categoryGroupKeyForLabel } from './model_detail'

// Fill alpha is kept low (~0.1) so each preset reads as a soft,
// translucent tint that lets the model canvas and any nodes beneath
// the note remain clearly visible. The border alpha stays close to 1
// so the dashed frame still reads as a crisp coloured edge regardless
// of the canvas content underneath.
export const COMMENT_COLOR_PRESETS = [
  { id: "amber", border: "rgba(255, 210, 40, 0.95)", fill: "rgba(255, 210, 40, 0.1)", label: "Amber" },
  { id: "cyan", border: "rgba(60, 200, 220, 0.95)", fill: "rgba(60, 200, 220, 0.1)", label: "Cyan" },
  { id: "magenta", border: "rgba(220, 100, 200, 0.95)", fill: "rgba(220, 100, 200, 0.1)", label: "Magenta" },
  { id: "lime", border: "rgba(140, 220, 90, 0.95)", fill: "rgba(140, 220, 90, 0.1)", label: "Lime" },
  { id: "coral", border: "rgba(255, 120, 100, 0.95)", fill: "rgba(255, 120, 100, 0.1)", label: "Coral" },
  { id: "violet", border: "rgba(160, 130, 255, 0.95)", fill: "rgba(160, 130, 255, 0.1)", label: "Violet" },
] as const

export type CommentColorId = (typeof COMMENT_COLOR_PRESETS)[number]["id"]

const DEFAULT_COMMENT_COLOR: CommentColorId = "amber"
const DEFAULT_COMMENT_FONT_SIZE = 17
const MIN_COMMENT_FONT_SIZE = 12
const MAX_COMMENT_FONT_SIZE = 36
/** Default size when a note is dropped from the toolbox (editor coordinates). */
const DROPPED_NOTE_WIDTH = 200
const DROPPED_NOTE_HEIGHT = 120

const COMPONENT_MIME = "application/x-landscapes-component"
const NOTE_MIME = "application/x-landscapes-note"

export function commentBorderColor(colorId?: string): string {
  const preset = COMMENT_COLOR_PRESETS.find(p => p.id === colorId)
  return preset?.border ?? COMMENT_COLOR_PRESETS[0].border
}

/**
 * The fill (background tint) for a sticky note, paired with the border
 * colour returned by `commentBorderColor`. Each preset already defines a
 * translucent fill — previously unused, which is why every note ended up
 * with the same hard-coded dark-amber background regardless of which
 * colour the user picked.
 */
export function commentFillColor(colorId?: string): string {
  const preset = COMMENT_COLOR_PRESETS.find(p => p.id === colorId)
  return preset?.fill ?? COMMENT_COLOR_PRESETS[0].fill
}

/** Model sticky notes (editor coordinates). Persisted on `Data` as `modelComments`. */
export interface ModelComment {
  id: string
  x: number
  y: number
  width: number
  height: number
  /** Palette key; omitted or unknown → amber */
  colorId?: CommentColorId | string
  fontSize?: number
  text?: string
}

type DataWithComments = Data & { modelComments?: ModelComment[] }

function normalizeComment(c: ModelComment): ModelComment {
  const colorId =
    typeof c.colorId === "string" && COMMENT_COLOR_PRESETS.some(p => p.id === c.colorId)
      ? (c.colorId as CommentColorId)
      : DEFAULT_COMMENT_COLOR
  const rawFontSize = typeof c.fontSize === "number" ? c.fontSize : DEFAULT_COMMENT_FONT_SIZE
  const fontSize = Math.min(MAX_COMMENT_FONT_SIZE, Math.max(MIN_COMMENT_FONT_SIZE, rawFontSize))
  return { ...c, colorId, fontSize }
}

function extractModelComments(model: Data | null): ModelComment[] {
  if (!model) return []
  const m = model as DataWithComments
  if (!Array.isArray(m.modelComments)) return []
  return m.modelComments.map(normalizeComment)
}

// Rete doesn't export `Transform`, so we have to re-define it ourselves
export interface Transform {
  k: number;
  x: number;
  y: number;
}
export interface ModelViewProps {
  visible: boolean
  initialTransform: Transform
  setTransform: (transform: Transform) => void
  initialModel: Data | null
  setModel: (model: Data) => void
  createOutputLayer: (id: number) => void
  deleteOutputLayer: (id: number) => void
  /**
   * Read the current style of the ModelOutputLayer linked to a Map layer
   * node id. Used by the agent `set_layer_style` tool to merge partial
   * colour updates. Returns null when no matching layer exists.
   */
  getOutputLayerStyle?: (nodeId: number) => {
    fill?: fillType
    colors?: Array<[number, number, number, number]>
    colorByLabel?: Record<string, [number, number, number, number]>
  } | null
  /**
   * Apply style fields to the ModelOutputLayer linked to a Map layer node
   * id. Returns false when no matching layer exists.
   */
  mutateOutputLayer?: (
    nodeId: number,
    data: {
      fill?: fillType
      colors?: Array<[number, number, number, number]>
      colorByLabel?: Record<string, [number, number, number, number]>
      comment?: string
    }
  ) => boolean
  /** Public descriptions already stored on Map layer nodes. */
  listMapLayerDescriptions?: () => Array<{ nodeId: number, name: string, comment: string }>
  saveMapLayer: SaveMapLayer
  setProcessing: (processing: boolean) => void
  autoProcessing: boolean
  process: boolean
  setProcess: (process: boolean) => void
  saveModel: SaveModel
  getDatasets: getDatasets
  getAnnotations: getAnnotationsType
  extent: Extent
  zoom: number
  mask: boolean
  maskLayer: string
  maskCQL: string
  maskShape: string | undefined
  permissions: ProjectPermissions
  getCustomExpressions: getExpressionsType
  debug: boolean
  extentList: AvailableExtent[]
  externalLink?: string
  setExtraInfo: (x: string | null) => void
  teamName: string
  /**
   * Active team's id — surfaced in the Toolbox info panel as part of the
   * deep links to team admin pages (Expressions, Extents, Paintbrush).
   */
  teamId: number
  /** Id of the project currently open in the editor (used when searching peers). */
  projectId?: number
  teamComponents: TeamComponentData[]
  /** Whether the team has a Groq API key configured (gates the AEGIS agent). */
  groqConfigured?: boolean
  /** Whether the team's selected AEGIS provider has an API key configured. */
  agentConfigured?: boolean
  /** Human-readable label for the team's effective AEGIS provider. */
  agentProvider?: string
  /** Human-readable label for the team's effective AEGIS model. */
  agentModel?: string
  /** Raw provider id ("groq" | "anthropic" | "deepseek") used to route the agent API. */
  agentProviderId?: string
  /** Raw model id sent to the provider API (e.g. "claude-sonnet-4-6"). */
  agentModelId?: string
  /** Called when the editor is ready so the parent can sync e.g. sidebar layer name → node.data.name */
  onEditorReady?: (api: ModelEditorApi) => void
  /** Live AEGIS action bridge, so the map-view AEGIS tab can share the same graph tools. */
  onAgentActionsReady?: (actions: AgentActions | undefined) => void
  /** Public view only. Lets AEGIS switch the area the map and model use. */
  publicExtentApiRef?: React.MutableRefObject<PublicExtentSwitchApi | null>
  /**
   * Fires once the initial model graph has been imported into Rete (or
   * immediately when there's no initial model). Used by the parent to
   * stagger heavy work — e.g. defer fetching saved datasets until after the
   * model graph finishes hydrating, since running both concurrently
   * saturates the main thread on large projects.
   */
  onInitialLoadComplete?: () => void
  /** Toolbox components registered for this model, so other UI can offer the same catalog. */
  onAvailableComponents?: (components: { name: string, label: string, category: string }[]) => void
  /**
   * Public view: a chat `#id` click should select the map layer for this
   * Map layer node instead of panning the hidden model canvas.
   */
  onFocusMapOutput?: (nodeId: number) => void
}

function cloneModelData(model: Data): Data {
  if (typeof structuredClone === "function") {
    return structuredClone(model) as Data
  }
  return JSON.parse(JSON.stringify(model)) as Data
}

function getMaxNodeMovement(prev: Data, next: Data): number {
  if (!prev.nodes || !next.nodes) return 0
  let max = 0
  const prevNodes = prev.nodes as any
  const nextNodes = next.nodes as any

  Object.keys(nextNodes).forEach(key => {
    const n2 = nextNodes[key]
    const n1 = prevNodes[key]
    if (!n1 || !n1.position || !n2.position) return
    const dx = (n2.position[0] ?? 0) - (n1.position[0] ?? 0)
    const dy = (n2.position[1] ?? 0) - (n1.position[1] ?? 0)
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist > max) max = dist
  })

  return max
}

/** Locate a node by numeric id, tolerating string-encoded ids from JSON. */
function findNodeById(nodes: Node[], id: number): Node | undefined {
  return nodes.find(n => n.id === id || String(n.id) === String(id))
}

/**
 * Resolve a socket on a node by name. We try, in order:
 *   1. literal key match (what `editor.toJSON()` emits)
 *   2. literal title / display-name match (what shows up in the UI and in
 *      the readable graph snapshot)
 *   3. the same matches against the inner part of a `"Display (key)"`
 *      string — the readable formatter writes inputs/outputs in that form
 *      when display name and key differ, and the LLM often copies the
 *      whole string into its tool args
 *   4. case-insensitive variants of any of the above
 * so the agent can use whichever label appears in its prompt context.
 */
function findOutput(node: Node, name: string): ReteOutput | undefined {
  return findIo(Array.from(node.outputs.values()), name)
}

function findInput(node: Node, name: string): ReteInput | undefined {
  return findIo(Array.from(node.inputs.values()), name)
}

function findIo<T extends { key: string; name: string }>(items: T[], rawName: string): T | undefined {
  const trimmed = rawName.trim()
  const candidates: string[] = [trimmed]
  // The readable graph snapshot writes "Display Name (key)" when display
  // and key disagree (e.g. an input called "Output" with key "in" shows
  // up as "Output (in)"). Pull both halves out so either component
  // matches on its own.
  const paren = trimmed.match(/^(.+?)\s*\(([^()]+)\)\s*$/)
  if (paren) {
    candidates.push(paren[1].trim(), paren[2].trim())
  }

  for (const candidate of candidates) {
    const hit = items.find(i => i.key === candidate) ?? items.find(i => i.name === candidate)
    if (hit) return hit
  }
  for (const candidate of candidates) {
    const lower = candidate.toLowerCase()
    const hit =
      items.find(i => i.key?.toLowerCase() === lower) ??
      items.find(i => i.name?.toLowerCase() === lower)
    if (hit) return hit
  }
  return undefined
}

/**
 * A Rete `Control` annotated with the optional `type` discriminator and
 * `props` bag every control in this codebase carries. Mirrors the shape
 * `project_model_readable.ts` already uses to format controls.
 */
type ControlWithMeta = {
  key: string
  type?: string
  props?: Record<string, unknown>
  putData?: (key: string, value: unknown) => void
  getData?: (key: string) => unknown
}

interface FoundControl {
  control: ControlWithMeta
  /** Human-readable identifier we matched against (for error messages). */
  matchedAs: string
}

/**
 * Resolve a control on a node by user-supplied name. We walk both the
 * direct node controls and the controls hanging off inputs, and build a
 * list of candidate identifiers for each: literal `control.key`, the
 * UI label (props.label / props.title), and the `Input.Control` combined
 * form for input-attached controls. Matching is tolerant of case and of
 * the `"Display (key)"` annotation pattern from the readable snapshot.
 */
function findNodeControl(node: Node, rawName: string): FoundControl | undefined {
  const trimmed = rawName.trim()
  if (!trimmed) return undefined

  const candidates = candidateControlIdentifiers(trimmed)
  const all = collectNodeControls(node)

  for (const candidate of candidates) {
    for (const entry of all) {
      if (entry.identifiers.includes(candidate)) {
        return { control: entry.control, matchedAs: candidate }
      }
    }
  }
  for (const candidate of candidates) {
    const lower = candidate.toLowerCase()
    for (const entry of all) {
      // Return the *canonical* identifier (preserving its original case)
      // so `applyControlValue` can do case-sensitive comparisons against
      // a control's expected sub-field names (e.g. "expose").
      const canonical = entry.identifiers.find(i => i.toLowerCase() === lower)
      if (canonical) {
        return { control: entry.control, matchedAs: canonical }
      }
    }
  }
  return undefined
}

function listNodeControlNames(node: Node): string[] {
  const names = new Set<string>()
  for (const entry of collectNodeControls(node)) {
    for (const id of entry.identifiers) names.add(id)
  }
  return Array.from(names)
}

interface CollectedControl {
  control: ControlWithMeta
  identifiers: string[]
}

function collectNodeControls(node: Node): CollectedControl[] {
  const out: CollectedControl[] = []
  node.controls.forEach(control => {
    const c = control as ControlWithMeta
    out.push({ control: c, identifiers: identifiersForControl(c) })
  })
  node.inputs.forEach(input => {
    if (!input.control) return
    const c = input.control as ControlWithMeta
    const base = identifiersForControl(c)
    // Input-attached controls are also addressable as "InputName.Label"
    // / "InputKey.Label", matching the way `project_model_readable.ts`
    // prefixes them with the input name.
    const prefixed: string[] = []
    for (const id of base) {
      prefixed.push(`${input.name}.${id}`, `${input.key}.${id}`)
    }
    out.push({ control: c, identifiers: [...base, ...prefixed] })
  })
  return out
}

function identifiersForControl(control: ControlWithMeta): string[] {
  const ids: string[] = []
  if (control.key) ids.push(control.key)
  const props = control.props ?? {}
  const label = typeof props.label === "string" ? props.label.trim() : ""
  const title = typeof props.title === "string" ? props.title.trim() : ""
  if (label) ids.push(label)
  if (title) ids.push(title)

  // Compound controls manage two `node.data` slots under one Rete
  // `Control` (compound key e.g. "Value:expose"). The readable snapshot
  // surfaces each slot as its own line — so the agent will naturally try
  // to address them that way. Register the sub-field identifiers here so
  // those matches succeed, and `applyControlValue` can route to the
  // right setter based on which identifier matched.
  switch (control.type) {
    case "NumericConstantRowControl":
      // Snapshot labels these literally; props don't expose names.
      ids.push("Value", "expose")
      break
    case "TextNumericControl": {
      const textTitle = typeof props.textTitle === "string" ? props.textTitle.trim() : ""
      const numericTitle = typeof props.numericTitle === "string" ? props.numericTitle.trim() : ""
      if (textTitle) ids.push(textTitle)
      if (numericTitle) ids.push(numericTitle)
      // Snapshot fallbacks when the titles are empty.
      ids.push("text", "value")
      break
    }
    case "TableEditorControl":
      // Readable snapshot labels the grid as `table:`; the Rete key is
      // `tableEditor`. Accept both so the agent can use either form.
      ids.push("table", "tableEditor")
      break
    case "OSMCustomQueryControl":
      ids.push("Build Query", "custom queries", "customQueries", "osmCustomQueries")
      break
  }
  return Array.from(new Set(ids))
}

function candidateControlIdentifiers(name: string): string[] {
  const out = [name]
  // Strip "(key)" annotation if the agent copied it verbatim from the
  // snapshot — same logic as findIo, applied to control names.
  const paren = name.match(/^(.+?)\s*\(([^()]+)\)\s*$/)
  if (paren) {
    out.push(paren[1].trim(), paren[2].trim())
  }
  return out
}

interface SelectOptionMeta { id: number | string; name: string }

/**
 * Push a new value into a control. Different Rete control classes expose
 * different setter shapes (setValue / setId / setExpose / etc.), so this
 * dispatches on `control.type` and falls back to a generic `setValue`
 * call for unknown types. Returns a friendly error rather than throwing
 * so the agent can surface it back to the user.
 */
function applyControlValue(
  control: ControlWithMeta,
  value: unknown,
  matchedAs: string
):
  | { ok: true }
  | { ok: false; error: string } {
  const type = control.type
  const props = control.props ?? {}

  switch (type) {
    case "SelectControl": {
      const options = (typeof props.getOptions === "function" ? (props.getOptions as () => SelectOptionMeta[])() : []) ?? []
      const matched = resolveSelectOption(options, value)
      if (!matched) {
        const optList = options.map(o => o.name).join(", ") || "(none)"
        return { ok: false, error: `Unknown option "${formatValue(value)}". Options: ${optList}.` }
      }
      const setId = props.setId as ((v: string) => unknown) | undefined
      setId?.(String(matched.id))
      const change = props.change as (() => void) | undefined
      change?.()
      return { ok: true }
    }
    case "CheckboxSelectControl": {
      const options = (typeof props.getOptions === "function" ? (props.getOptions as () => SelectOptionMeta[])() : []) ?? []
      const raw = Array.isArray(value) ? value : [value]
      const ids: string[] = []
      const unknowns: string[] = []
      for (const v of raw) {
        const m = resolveSelectOption(options, v)
        if (m) ids.push(String(m.id))
        else unknowns.push(formatValue(v))
      }
      if (unknowns.length) {
        const optList = options.map(o => o.name).join(", ") || "(none)"
        return { ok: false, error: `Unknown option(s): ${unknowns.join(", ")}. Options: ${optList}.` }
      }
      const setId = props.setId as ((v: string[]) => unknown) | undefined
      setId?.(ids)
      const change = props.change as (() => void) | undefined
      change?.()
      return { ok: true }
    }
    case "BooleanControl": {
      const setValue = props.setValue as ((v: boolean) => void) | undefined
      setValue?.(toBoolean(value))
      return { ok: true }
    }
    case "ExposeToggleControl": {
      const setExpose = props.setExpose as ((v: boolean) => void) | undefined
      setExpose?.(toBoolean(value))
      return { ok: true }
    }
    case "TextControl":
    case "DateControl": {
      const setValue = props.setValue as ((v: string) => void) | undefined
      setValue?.(String(value))
      return { ok: true }
    }
    case "NumericConstantRowControl": {
      // The control bundles two `node.data` slots: "Value" (numeric
      // string) and "expose" (boolean for the eye toggle). Pick the
      // setter that matches what the agent actually asked for.
      if (matchedAs === "expose") {
        const setExpose = props.setExpose as ((v: boolean) => void) | undefined
        setExpose?.(toBoolean(value))
        return { ok: true }
      }
      const setValue = props.setValue as ((v: string) => void) | undefined
      setValue?.(String(value))
      return { ok: true }
    }
    case "TextNumericControl": {
      const textTitle = typeof props.textTitle === "string" ? props.textTitle.trim() : ""
      const numericTitle = typeof props.numericTitle === "string" ? props.numericTitle.trim() : ""
      const setNumeric = props.setNumericValue as ((v: string) => void) | undefined
      const setText = props.setTextValue as ((v: string) => void) | undefined

      const m = matchedAs.toLowerCase()
      const wantsText = m === "text" || (textTitle !== "" && m === textTitle.toLowerCase())
      const wantsNumeric = m === "value" || (numericTitle !== "" && m === numericTitle.toLowerCase())

      if (wantsText && setText) {
        setText(String(value))
        return { ok: true }
      }
      if (wantsNumeric && setNumeric) {
        setNumeric(String(value))
        return { ok: true }
      }
      // Compound key with no explicit sub-field — guess numeric-first
      // when the value parses as a number, otherwise treat it as text.
      if (setNumeric && typeof value === "number") {
        setNumeric(String(value))
        return { ok: true }
      }
      if (setNumeric && value !== "" && !Number.isNaN(Number(value))) {
        setNumeric(String(value))
        return { ok: true }
      }
      if (setText) {
        setText(String(value))
        return { ok: true }
      }
      return { ok: false, error: "TextNumericControl has no usable setter." }
    }
    case "TableEditorControl": {
      // Table grids live in node.data tableRows/tableColumns/tableCells.
      // The generic putData(control.key) fallback would write to
      // "tableEditor" and appear to succeed without updating the grid —
      // so handle this type explicitly via importTable.
      const parsed = parseTableControlValue(value)
      if (!parsed.ok) return parsed
      const importTable = props.importTable as
        | ((data: TableSpreadsheetData) => void)
        | undefined
      if (typeof importTable !== "function") {
        return { ok: false, error: "TableEditorControl has no importTable setter." }
      }
      importTable(parsed.data)
      return { ok: true }
    }
    case "OSMCustomQueryControl": {
      const parsed = parseOsmCustomQueryValue(value)
      if (!parsed.ok) return parsed
      const setQueries = props.setQueries as ((queries: unknown) => void) | undefined
      if (typeof setQueries === "function") {
        setQueries(parsed.queries)
        return { ok: true }
      }
      if (typeof control.putData === "function" && control.key) {
        control.putData(control.key, parsed.queries)
        return { ok: true }
      }
      return { ok: false, error: "OSM custom query control has no setter." }
    }
    default: {
      // Generic fallback — works for any custom control that exposes a
      // `setValue` prop, and for anything else falls back to writing
      // directly to `node.data` via `putData(key, …)`.
      const setValue = props.setValue as ((v: unknown) => void) | undefined
      if (typeof setValue === "function") {
        setValue(value)
        return { ok: true }
      }
      if (typeof control.putData === "function" && control.key) {
        control.putData(control.key, value)
        return { ok: true }
      }
      return {
        ok: false,
        error: `Control type "${type ?? "unknown"}" is not supported by set_control.`,
      }
    }
  }
}

function resolveSelectOption(
  options: SelectOptionMeta[],
  value: unknown
): SelectOptionMeta | undefined {
  if (value === undefined || value === null) return undefined
  const str = String(value).trim()
  const lower = str.toLowerCase()
  return (
    options.find(o => String(o.id) === str) ??
    options.find(o => o.name === str) ??
    options.find(o => String(o.id).toLowerCase() === lower) ??
    options.find(o => o.name?.toLowerCase() === lower)
  )
}

function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const v = value.trim().toLowerCase()
    return v === "true" || v === "yes" || v === "on" || v === "1"
  }
  return Boolean(value)
}

function formatValue(value: unknown): string {
  if (typeof value === "string") return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function parseTableCellValue(raw: unknown): TableCell {
  if (raw === null || raw === undefined || raw === "") return null
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null
  if (typeof raw === "boolean") return raw ? 1 : 0
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (trimmed === "") return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/**
 * Accept several shapes the agent may emit for a Table control:
 *  1. `{ rows, columns, cells }` — explicit spreadsheet payload
 *  2. `{ rowName: { colName: number, … }, … }` — nested row→column map
 *  3. A JSON string of either of the above
 */
function parseTableControlValue(
  value: unknown
): { ok: true; data: TableSpreadsheetData } | { ok: false; error: string } {
  let raw: unknown = value
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (!trimmed) {
      return { ok: false, error: "Table value is an empty string." }
    }
    try {
      raw = JSON.parse(trimmed)
    } catch {
      return {
        ok: false,
        error:
          "Table value must be a JSON object (row→column map or {rows, columns, cells}).",
      }
    }
  }

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {
      ok: false,
      error:
        "Table value must be an object: either { rows, columns, cells } or " +
        "{ rowName: { colName: number, … }, … }.",
    }
  }

  const obj = raw as Record<string, unknown>

  // Explicit spreadsheet shape.
  if ("rows" in obj || "columns" in obj || "cells" in obj) {
    const rows = Array.isArray(obj.rows) ? obj.rows.map(v => String(v ?? "")) : []
    const columns = Array.isArray(obj.columns)
      ? obj.columns.map(v => String(v ?? ""))
      : []
    const rawCells = Array.isArray(obj.cells) ? obj.cells : []
    if (rows.length === 0 && columns.length === 0) {
      return { ok: false, error: "Table { rows, columns, cells } needs at least one row or column." }
    }
    const cells: TableCell[][] = rows.map((_r, r) => {
      const row = Array.isArray(rawCells[r]) ? (rawCells[r] as unknown[]) : []
      return columns.map((_c, c) => parseTableCellValue(row[c]))
    })
    return { ok: true, data: { rows, columns, cells } }
  }

  // Nested row → { column → value } map (what the agent commonly emits).
  const rows = Object.keys(obj)
  if (rows.length === 0) {
    return { ok: false, error: "Table map is empty." }
  }

  const columns: string[] = []
  const seen = new Set<string>()
  for (const rowName of rows) {
    const rowObj = obj[rowName]
    if (!rowObj || typeof rowObj !== "object" || Array.isArray(rowObj)) {
      return {
        ok: false,
        error:
          `Row "${rowName}" must be an object of column→number pairs ` +
          `(got ${Array.isArray(rowObj) ? "array" : typeof rowObj}).`,
      }
    }
    for (const colName of Object.keys(rowObj as Record<string, unknown>)) {
      if (!seen.has(colName)) {
        seen.add(colName)
        columns.push(colName)
      }
    }
  }

  if (columns.length === 0) {
    return { ok: false, error: "Table map has rows but no column values." }
  }

  const cells: TableCell[][] = rows.map(rowName => {
    const rowObj = obj[rowName] as Record<string, unknown>
    return columns.map(colName => parseTableCellValue(rowObj[colName]))
  })

  return { ok: true, data: { rows, columns, cells } }
}

/**
 * Resolve a registered component by the name the agent (or user) supplied.
 * Accepts the canonical `component.name`, the toolbox `contextMenuName`
 * (e.g. "Greater (>)" ), and tolerant variants — case-insensitive match,
 * and stripping a trailing "(…)" suffix so "Greater (>)" resolves to
 * "Greater".
 */
function resolveRegisteredComponent(
  components: BaseComponent[],
  rawName: string
): BaseComponent | undefined {
  const trimmed = rawName.trim()
  if (!trimmed) return undefined

  const candidates: string[] = [trimmed]
  const paren = trimmed.match(/^(.+?)\s*\(([^()]+)\)\s*$/)
  if (paren) {
    candidates.push(paren[1].trim())
  }

  for (const candidate of candidates) {
    const hit = components.find(c => c.name === candidate)
    if (hit) return hit
  }
  for (const candidate of candidates) {
    const hit = components.find(c => c.contextMenuName?.trim() === candidate)
    if (hit) return hit
  }
  for (const candidate of candidates) {
    const lower = candidate.toLowerCase()
    const hit = components.find(
      c =>
        c.name.toLowerCase() === lower ||
        (c.contextMenuName?.trim().toLowerCase() ?? "") === lower
    )
    if (hit) return hit
  }
  return undefined
}

type NodeFxKind = "add" | "remove"

/**
 * Pause between consecutive agent-initiated `add_component` calls so a batched
 * plan drops nodes onto the canvas one at a time (cascade) rather than all at
 * once. Each `addComponent` awaits this, so dependent connect/set calls still
 * run only after the node exists.
 */
const AGENT_ADD_STAGGER_MS = 150

/**
 * Spawn a small particle burst at the given editor-area coordinates.
 * Used to give a bit of visual feedback when a node is added or
 * removed (either by the user or by the agent). Particles are appended
 * to the editor's pan/zoom area so they inherit its CSS transform and
 * land on top of the canvas in the right place even when the user has
 * zoomed/panned. Uses inline styles + the Web Animations API so we
 * don't have to ship any CSS, and each particle removes itself on
 * animation finish so the DOM stays clean even during rapid changes.
 */
function spawnNodeFx(
  host: HTMLElement,
  position: { x: number; y: number },
  kind: NodeFxKind
) {
  // Nodes anchor at their top-left corner — nudge the burst toward the
  // visual centre of a typical node body. Doesn't need to be exact;
  // it's a quick flourish, not a precise overlay.
  const cx = position.x + 110
  const cy = position.y + 40

  const palette: Record<NodeFxKind, string[]> = {
    add:    ["#6ecf8a", "#9ce6ad", "#fff4a3"],
    remove: ["#ff8a80", "#ffb74d", "#ffd180"],
  }
  const colors = palette[kind]
  const count = 10
  const baseDuration = 550
  const spread = kind === "add" ? 38 : 48

  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4
    const distance = spread + Math.random() * spread * 0.7
    const dx = Math.cos(angle) * distance
    const dy = Math.sin(angle) * distance
    const size = 4 + Math.random() * 4
    const color = colors[Math.floor(Math.random() * colors.length)]
    const duration = baseDuration + Math.random() * 200

    const dot = document.createElement("div")
    dot.style.position = "absolute"
    dot.style.left = `${cx}px`
    dot.style.top = `${cy}px`
    dot.style.width = `${size}px`
    dot.style.height = `${size}px`
    dot.style.marginLeft = `${-size / 2}px`
    dot.style.marginTop = `${-size / 2}px`
    dot.style.borderRadius = "50%"
    dot.style.background = color
    dot.style.boxShadow = `0 0 ${kind === "add" ? 6 : 4}px ${color}`
    dot.style.pointerEvents = "none"
    dot.style.zIndex = "1000"
    dot.style.willChange = "transform, opacity"
    host.appendChild(dot)

    // Web Animations API > CSS transition: no race between insert + style
    // mutation, easier cleanup, and doesn't pollute the stylesheet.
    const finalScale = kind === "add" ? 0.2 : 0.05
    const anim = dot.animate(
      [
        { transform: "translate(0, 0) scale(1)", opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) scale(${finalScale})`, opacity: 0 },
      ],
      {
        duration,
        easing: "cubic-bezier(0.2, 0.7, 0.4, 1)",
        fill: "forwards",
      }
    )
    const cleanup = () => dot.remove()
    anim.onfinish = cleanup
    anim.oncancel = cleanup
  }
}

function formatMapLayerDescriptions(
  notes: Array<{ nodeId: number, name: string, comment: string }>
): string {
  if (notes.length === 0) return ""
  const lines = notes.map(note => {
    const body = note.comment.replace(/\n/g, "\n  ")
    return `- ${note.name} #${note.nodeId}: ${body}`
  })
  return "\n=== Public layer descriptions ===\n" + lines.join("\n") + "\n"
}

export function ModelView({ visible, initialTransform, setTransform, initialModel, setModel, createOutputLayer, deleteOutputLayer, getOutputLayerStyle, mutateOutputLayer, listMapLayerDescriptions, saveMapLayer, setProcessing, autoProcessing, process, setProcess, saveModel, getDatasets, getAnnotations, extent, zoom, mask, maskLayer, maskCQL, maskShape, permissions, getCustomExpressions, debug, extentList, externalLink, setExtraInfo, teamName, teamId, projectId, teamComponents, groqConfigured, agentConfigured, agentProvider, agentModel, agentProviderId, agentModelId, onEditorReady, onAgentActionsReady, publicExtentApiRef, onInitialLoadComplete, onAvailableComponents, onFocusMapOutput }: ModelViewProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [editor, setEditor] = React.useState<NodeEditor>()
  const [engine, setEngine] = React.useState<Engine>()
  const [legendVisible, setLegendVisible] = React.useState(true)
  const [registeredComponents, setRegisteredComponents] = React.useState<BaseComponent[]>([])
  const publicShareSettings = usePublicShareSettings()
  const publicShareSettingsRef = React.useRef(publicShareSettings)
  publicShareSettingsRef.current = publicShareSettings
  const onFocusMapOutputRef = React.useRef(onFocusMapOutput)
  onFocusMapOutputRef.current = onFocusMapOutput
  const [modelHistory, setModelHistory] = React.useState<Data[]>([])
  const [historyIndex, setHistoryIndex] = React.useState<number>(-1)
  const historyIndexRef = React.useRef<number>(-1)
  const isApplyingHistoryRef = React.useRef(false)
  /**
   * Set true for the synchronous duration of an agent-initiated node add /
   * remove so the `nodecreated` / `noderemoved` listeners know the change came
   * from AEGIS rather than the user. The particle burst is gated on this — we
   * only want the flourish for agent edits, not ordinary user interactions.
   */
  const isAgentActionRef = React.useRef(false)
  /**
   * Accumulates the ids of nodes the agent created during the current turn so
   * `autoLayout` can reposition *only* those, arranging them around the
   * existing (user-placed) nodes which stay put. Cleared after each layout.
   */
  const agentAddedNodesRef = React.useRef<Set<number>>(new Set())
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [modelComments, setModelComments] = React.useState<ModelComment[]>(() => extractModelComments(initialModel))
  const modelCommentsRef = React.useRef<ModelComment[]>(modelComments)
  /** Mount inside Rete `area.el` so comments inherit viewport translate/scale with the graph. */
  const [commentPortalHost, setCommentPortalHost] = React.useState<HTMLDivElement | null>(null)
  /** Rubber-band while Ctrl/Cmd+dragging, in editor (model) coordinates. */
  const [commentDrawPreview, setCommentDrawPreview] = React.useState<{ x: number; y: number; width: number; height: number } | null>(null)
  const [commentMenuId, setCommentMenuId] = React.useState<string | null>(null)
  const [commentTextDraft, setCommentTextDraft] = React.useState("")
  const [resizingCommentId, setResizingCommentId] = React.useState<string | null>(null)
  const textMeasureCanvasRef = React.useRef<HTMLCanvasElement | null>(null)
  /**
   * One-shot "fit to screen on load" gate. Flipped to true after the initial
   * model graph has been imported into Rete (or set immediately when there's
   * no initial model at all). Drives a separate effect that dispatches
   * `model:zoomToFit` exactly once, after the editor has nodes and the canvas
   * has real dimensions.
   */
  const [initialModelLoaded, setInitialModelLoaded] = React.useState(false)
  const didInitialFitRef = React.useRef(false)
  /**
   * Guards `onInitialLoadComplete` against firing more than once per editor
   * lifecycle — the parent uses it to release deferred dataset fetches and
   * we don't want a re-render path to re-trigger that work.
   */
  const didNotifyInitialLoadRef = React.useRef(false)
  const notifyInitialLoadComplete = React.useCallback(() => {
    if (didNotifyInitialLoadRef.current) return
    didNotifyInitialLoadRef.current = true
    onInitialLoadComplete?.()
  }, [onInitialLoadComplete])
  /**
   * Currently "focused" node — the one whose connected chain (both upstream
   * inputs and downstream outputs) we highlight. Activated only when the user
   * clicks a node while holding the `F` key ("focus mode" gesture). A plain
   * click on a node — or on the canvas background — clears the focus and
   * returns to normal selection.
   */
  const [focusedNodeId, setFocusedNodeId] = React.useState<number | null>(null)
  const focusedNodeIdRef = React.useRef<number | null>(null)
  React.useEffect(() => {
    focusedNodeIdRef.current = focusedNodeId
  }, [focusedNodeId])
  /**
   * Tracks whether the `F` key is currently pressed — read synchronously
   * inside Rete's `nodeselected` handler to decide whether the click counts
   * as a "focus mode" gesture. Kept as a ref (not state) so we don't trigger
   * re-renders on every keystroke. Listeners ignore key events targeted at
   * text inputs / textareas / contenteditable elements so typing the letter
   * "f" into the node-name field or any other input doesn't arm focus mode.
   */
  const fKeyHeldRef = React.useRef(false)
  React.useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null
      if (!el) return false
      const tag = el.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
      if (el.isContentEditable) return true
      return false
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "f" && e.key !== "F") return
      if (isEditableTarget(e.target)) return
      fKeyHeldRef.current = true
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== "f" && e.key !== "F") return
      fKeyHeldRef.current = false
    }
    // Defensive reset: if the user alt-tabs while holding F (or any other
    // event causes us to miss the keyup), don't leave focus-mode armed.
    const onBlur = () => {
      fKeyHeldRef.current = false
    }
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("keyup", onKeyUp)
    window.addEventListener("blur", onBlur)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("keyup", onKeyUp)
      window.removeEventListener("blur", onBlur)
    }
  }, [])

  React.useEffect(() => {
    modelCommentsRef.current = modelComments
  }, [modelComments])

  /** Set in the editor init effect so Ctrl/Cmd+drag can call the same persist + history path as graph edits. */
  const writeModelRef = React.useRef<((options?: { movement?: boolean }) => void) | undefined>(undefined)

  React.useEffect(() => {
    historyIndexRef.current = historyIndex
  }, [historyIndex])

  React.useEffect(() => {
    if (ref.current === null) return

    const editor = new NodeEditor("landscapes@1.0.0", ref.current)
    editor.view.area.transform = initialTransform
    editor.view.area.update()
    const updateTransform = () => setTransform(editor.view.area.transform)
    editor.on("zoomed", updateTransform)
    editor.on("translated", updateTransform)
    editor.use(ConnectionPlugin)
    editor.use(MinimapPlugin)
    editor.use(ReactRenderPlugin, { component: NodeComponent })
    editor.use(ContextMenuPlugin, {
      searchBar: false, // Too buggy
      delay: 100,
      allocate: (component: BaseComponent) => {
        if (!isComponentPubliclyAvailable(component, publicShareSettingsRef.current)) return null
        return component.category ? [component.category] : []
      },
      rename: (component: BaseComponent) =>
        component.contextMenuName || component.name,
    })

    const engine = new Engine("landscapes@1.0.0")
    const components = createDefaultComponents(saveMapLayer, saveModel, getDatasets, getAnnotations, extent, zoom, mask, maskLayer, maskCQL, permissions, getCustomExpressions, debug, maskShape, extentList, externalLink, setExtraInfo, teamName, teamComponents)
    components.forEach(component => {
      editor.register(component)
      engine.register(component)
    })
    setRegisteredComponents(components)
    onAvailableComponents?.(components.map(component => ({
      name: component.name,
      label: component.contextMenuName?.trim() || component.name,
      category: component.category || "Other",
    })))
    const detachConnectionMenu = enableConnectionDeleteContextMenu(editor)

    const commentHost = document.createElement("div")
    commentHost.className = "model-editor-comment-portal-host"
    commentHost.setAttribute("aria-hidden", "true")
    editor.view.area.el.appendChild(commentHost)
    setCommentPortalHost(commentHost)

    const addNodeSyncListeners = () => {
      editor.on("nodecreated", e => {
        if (e.name === "Map layer") {
          createOutputLayer(e.id)
        }
        // Tiny visual flourish on add — only for agent-initiated edits
        // (`isAgentActionRef`). Also skipped during undo/redo
        // (`isApplyingHistoryRef`) because `fromJSON` replays the entire
        // graph and would otherwise burst-spawn a particle per node.
        if (isAgentActionRef.current && !isApplyingHistoryRef.current) {
          // Remember this as a fresh agent add so auto-layout only moves it.
          agentAddedNodesRef.current.add(e.id)
          const host = editor.view?.area?.el
          const pos = e.position
          if (host && Array.isArray(pos)) {
            spawnNodeFx(host, { x: pos[0], y: pos[1] }, "add")
          }
        }
      })

      editor.on("noderemoved", e => {
        if (e.name === "Map layer") {
          deleteOutputLayer(e.id)
        }
        if (isAgentActionRef.current && !isApplyingHistoryRef.current) {
          const host = editor.view?.area?.el
          const pos = e.position
          if (host && Array.isArray(pos)) {
            spawnNodeFx(host, { x: pos[0], y: pos[1] }, "remove")
          }
        }
      })
    }

    const writeModel = (options?: { movement?: boolean }) => {
      const json = editor.toJSON() as Data
      syncCatMapValuesByLabelInReteModelData(json)
      // Rete's toJSON() embeds live node.data references — clone before stripping
      // transient UI flags or we'd collapse open tables on every process/save.
      const snapshot = cloneModelData(json) as DataWithComments
      stripTransientTableNodeData(snapshot)
      snapshot.modelComments = modelCommentsRef.current
      setModel(snapshot)

      if (isApplyingHistoryRef.current) {
        return
      }

      setModelHistory(prev => {
        const currentIndex = historyIndexRef.current >= 0 ? historyIndexRef.current : prev.length - 1
        const last = currentIndex >= 0 ? prev[currentIndex] : undefined
        const isMovement = options?.movement ?? false

        if (last) {
          if (!isMovement && JSON.stringify(last) === JSON.stringify(snapshot)) {
            return prev
          }

          if (isMovement) {
            const maxMove = getMaxNodeMovement(last, snapshot)
            // Ignore tiny drags; threshold in editor coordinate space
            if (maxMove < 10) {
              return prev
            }
          }
        }

        const truncated = currentIndex >= 0 ? prev.slice(0, currentIndex + 1) : []
        const next = [...truncated, snapshot]
        const maxHistoryEntries = 100
        const bounded = next.length > maxHistoryEntries ? next.slice(next.length - maxHistoryEntries) : next
        setHistoryIndex(bounded.length - 1)
        return bounded
      })
    }
    writeModelRef.current = writeModel
    const addWriteModelListener = () => {
      editor.on(
        ["nodecreated", "noderemoved", "connectioncreated", "connectionremoved", "process"],
        () => writeModel()
      )

      // Only track node movement when a drag operation finishes, and only
      // when the movement is large enough (handled inside writeModel).
      editor.on(
        ["nodedragged"],
        () => writeModel({ movement: true })
      )
    }

    const findEditorNode = (nodeId: number) =>
      editor.nodes.find(n => n.id === nodeId || String(n.id) === String(nodeId))

    const gatherWorkerInputsForNode = (node: Node): WorkerInputs => {
      const inputs: WorkerInputs = {}
      node.inputs.forEach(input => {
        const key = String(input.key)
        const values: unknown[] = []
        input.connections.forEach(conn => {
          const sourceNodeId = conn.output.node?.id
          if (sourceNodeId === undefined) return
          const sourceNode = editor.nodes.find(n => n.id === sourceNodeId)
          if (!sourceNode) return
          const outKey = conn.output.key as string
          const outputData = (sourceNode as Node & { outputData?: WorkerOutputs }).outputData
          const value = outputData?.[outKey] ?? sourceNode.meta?.output
          if (value !== undefined) {
            values.push(value)
          }
        })
        if (values.length > 0) {
          inputs[key] = values
        }
      })
      return inputs
    }

    const reprocessNumericConstantNode = async (node: Node) => {
      const component = editor.components.get("Numeric constant") as BaseComponent | undefined
      if (!component) {
        return
      }
      const outputs: WorkerOutputs = {}
      await component.worker({ id: node.id, data: node.data } as NodeData, {}, outputs)
      ;(node as Node & { outputData?: WorkerOutputs }).outputData = outputs
    }

    const reprocessCategoricalToNumericNode = async (node: Node) => {
      const component = editor.components.get(CATEGORICAL_TO_NUMERIC_COMPONENT_NAME) as BaseComponent | undefined
      if (!component) {
        return
      }
      const inputs = gatherWorkerInputsForNode(node)
      const catInput = inputs.in?.[0]
      if (!(catInput instanceof CategoricalTileGrid)) {
        return
      }
      const outputs: WorkerOutputs = {}
      await component.worker({ id: node.id, data: node.data } as NodeData, inputs, outputs)
      if (outputs.out !== undefined) {
        node.meta.output = outputs.out
      }
      ;(node as Node & { outputData?: WorkerOutputs }).outputData = outputs
    }

    type ReteControlWithUpdate = { update?: () => Promise<void> }
    type ReteNodeWithUpdate = Node & { update?: () => Promise<void> }

    const refreshCategoricalToNumericNodeView = async (
      node: Node,
      fields: { mapValues?: boolean; expose?: boolean }
    ) => {
      if (fields.mapValues) {
        node.inputs.forEach(input => {
          if (!String(input.key).startsWith("catVal_")) return
          const controlWithUpdate = input.control as ReteControlWithUpdate | undefined
          if (typeof controlWithUpdate?.update === "function") {
            void controlWithUpdate.update()
          }
        })
      }
      if (fields.expose) {
        const exposeControl = node.controls.get("expose") as ReteControlWithUpdate | undefined
        if (typeof exposeControl?.update === "function") {
          await exposeControl.update()
        }
      }
      if (fields.mapValues || fields.expose) {
        const nodeWithUpdate = node as ReteNodeWithUpdate
        if (typeof nodeWithUpdate.update === "function") {
          await nodeWithUpdate.update()
        }
      }
    }

    const refreshPayoffMatrixNodeView = async (
      node: Node,
      fields: { matrix?: boolean; expose?: boolean }
    ) => {
      if (fields.matrix) {
        const matrixControl = node.controls.get("payoffMatrix") as ReteControlWithUpdate | undefined
        if (typeof matrixControl?.update === "function") {
          await matrixControl.update()
        }
      }
      if (fields.expose) {
        const exposeControl = node.controls.get("expose") as ReteControlWithUpdate | undefined
        if (typeof exposeControl?.update === "function") {
          await exposeControl.update()
        }
      }
      if (fields.matrix || fields.expose) {
        const nodeWithUpdate = node as ReteNodeWithUpdate
        if (typeof nodeWithUpdate.update === "function") {
          await nodeWithUpdate.update()
        }
      }
    }

    const readEditorTableRows = (node: Node): string[] => {
      const value = node.data[TABLE_ROWS_KEY]
      return Array.isArray(value) ? (value as string[]) : []
    }

    const readEditorTableColumns = (node: Node): string[] => {
      const value = node.data[TABLE_COLUMNS_KEY]
      return Array.isArray(value) ? (value as string[]) : []
    }

    const readEditorTableCells = (node: Node): TableCell[][] => {
      const rows = readEditorTableRows(node)
      const columns = readEditorTableColumns(node)
      const raw = node.data[TABLE_CELLS_KEY]
      const existing = Array.isArray(raw) ? (raw as TableCell[][]) : []
      return rows.map((_r, r) =>
        columns.map((_c, c) => {
          const value = existing[r] ? existing[r][c] : null
          return typeof value === "number" && Number.isFinite(value) ? value : null
        })
      )
    }

    const writeEditorTableCells = (node: Node, cells: TableCell[][]) => {
      node.data[TABLE_CELLS_KEY] = cells
    }

    const isEditorTableColumnsLocked = (node: Node): boolean => {
      const refInput = node.inputs.get("ref")
      return !!refInput && refInput.connections.length > 0
    }

    const refreshTableNodeView = async (
      node: Node,
      fields: { table?: boolean; expose?: boolean }
    ) => {
      if (fields.table) {
        const tableControl = node.controls.get("tableEditor") as ReteControlWithUpdate | undefined
        if (typeof tableControl?.update === "function") {
          await tableControl.update()
        }
      }
      if (fields.expose) {
        const exposeControl = node.controls.get("expose") as ReteControlWithUpdate | undefined
        if (typeof exposeControl?.update === "function") {
          await exposeControl.update()
        }
      }
      if (fields.table || fields.expose) {
        const nodeWithUpdate = node as ReteNodeWithUpdate
        if (typeof nodeWithUpdate.update === "function") {
          await nodeWithUpdate.update()
        }
      }
    }

    const refreshNumericConstantNodeView = async (
      node: Node,
      fields: { value?: boolean; name?: boolean }
    ) => {
      if (fields.value) {
        const rowControl =
          node.controls.get("Value:expose") ??
          Array.from(node.controls.values()).find(
            c => (c as ReteControlWithUpdate & { type?: string }).type === "NumericConstantRowControl"
          )
        const valueControl = rowControl ?? node.controls.get("Value")
        const controlWithUpdate = valueControl as ReteControlWithUpdate | undefined
        if (typeof controlWithUpdate?.update === "function") {
          await controlWithUpdate.update()
        }
      }
      if (fields.name) {
        const nodeWithUpdate = node as ReteNodeWithUpdate
        if (typeof nodeWithUpdate.update === "function") {
          await nodeWithUpdate.update()
        }
      }
    }

    const notifyEditorReady = () => {
      onEditorReady?.({
        updateNodeName: (nodeId: number, name: string) => {
          const node = findEditorNode(nodeId)
          if (node) {
            node.data.name = name
            writeModel()
          }
        },
        updateNumericConstant: (nodeId: number, updates) => {
          const node = findEditorNode(nodeId)
          if (!node || node.name !== "Numeric constant") {
            return
          }
          const refreshFields: { value?: boolean; name?: boolean } = {}
          if (updates.name !== undefined) {
            node.data.name = updates.name
            refreshFields.name = true
          }
          if (updates.value !== undefined) {
            node.data.Value = updates.value
            const rowControl = node.controls.get("Value:expose") as { putData?: (key: string, value: unknown) => void } | undefined
            if (rowControl?.putData) {
              rowControl.putData("Value", updates.value)
            } else {
              const valueControl = node.controls.get("Value") as { putData?: (key: string, value: unknown) => void } | undefined
              valueControl?.putData?.("Value", updates.value)
            }
            refreshFields.value = true
          }
          if (updates.expose !== undefined) {
            node.data.expose = updates.expose
            writeModel()
            return
          }
          void reprocessNumericConstantNode(node)
            .then(() => refreshNumericConstantNodeView(node, refreshFields))
            .then(() => writeModel())
        },
        updateCategoricalToNumeric: (nodeId: number, updates) => {
          const node = findEditorNode(nodeId)
          if (!node || node.name !== CATEGORICAL_TO_NUMERIC_COMPONENT_NAME) {
            return
          }
          const refreshFields: { mapValues?: boolean; expose?: boolean } = {}
          if (updates.expose !== undefined) {
            node.data.expose = updates.expose
            refreshFields.expose = true
            void refreshCategoricalToNumericNodeView(node, refreshFields).then(() => writeModel())
            return
          }
          if (updates.mapValue !== undefined) {
            const { categoryValue, numericValue } = updates.mapValue
            const catInput = node.inputs.get(`catVal_${categoryValue}`)
            if (catInput?.hasConnection()) {
              return
            }
            const dataKey = mapValDataKeyForCategoryValue(categoryValue)
            node.data[dataKey] = numericValue
            node.inputs.forEach(input => {
              if (String(input.key) !== `catVal_${categoryValue}`) return
              const control = input.control as { putData?: (key: string, value: unknown) => void } | undefined
              control?.putData?.(dataKey, numericValue)
            })
            persistCatMapValuesByLabelOnNode(node)
            refreshFields.mapValues = true
          }
          void reprocessCategoricalToNumericNode(node)
            .then(() => refreshCategoricalToNumericNodeView(node, refreshFields))
            .then(() => writeModel())
        },
        importCategoricalDetailMatrix: updates => {
          if (!updates.length) {
            return
          }

          const updatesByNodeId = new Map<
            number,
            Array<{ categoryValue: number; numericValue: string }>
          >()
          for (const update of updates) {
            const list = updatesByNodeId.get(update.nodeId) ?? []
            list.push({ categoryValue: update.categoryValue, numericValue: update.numericValue })
            updatesByNodeId.set(update.nodeId, list)
          }

          const nodesToRefresh: Node[] = []
          updatesByNodeId.forEach((mapValues, nodeId) => {
            const node = findEditorNode(nodeId)
            if (!node || node.name !== CATEGORICAL_TO_NUMERIC_COMPONENT_NAME) {
              return
            }

            let changed = false
            for (const { categoryValue, numericValue } of mapValues) {
              const catInput = node.inputs.get(`catVal_${categoryValue}`)
              if (catInput?.hasConnection()) {
                continue
              }
              const dataKey = mapValDataKeyForCategoryValue(categoryValue)
              node.data[dataKey] = numericValue
              node.inputs.forEach(input => {
                if (String(input.key) !== `catVal_${categoryValue}`) {
                  return
                }
                const control = input.control as { putData?: (key: string, value: unknown) => void } | undefined
                control?.putData?.(dataKey, numericValue)
              })
              changed = true
            }

            if (changed) {
              persistCatMapValuesByLabelOnNode(node)
              nodesToRefresh.push(node)
            }
          })

          if (!nodesToRefresh.length) {
            return
          }

          void Promise.all(nodesToRefresh.map(node => reprocessCategoricalToNumericNode(node)))
            .then(() =>
              Promise.all(
                nodesToRefresh.map(node =>
                  refreshCategoricalToNumericNodeView(node, { mapValues: true })
                )
              )
            )
            .then(() => writeModel())
        },
        updatePayoffMatrix: (nodeId: number, updates) => {
          const node = findEditorNode(nodeId)
          if (!node || node.name !== PAYOFF_MATRIX_COMPONENT_NAME) {
            return
          }
          const refreshFields: { matrix?: boolean; expose?: boolean } = {}
          if (updates.expose !== undefined) {
            node.data.expose = updates.expose
            refreshFields.expose = true
            void refreshPayoffMatrixNodeView(node, refreshFields).then(() => writeModel())
            return
          }
          if (updates.cell !== undefined) {
            const { aValue, bValue, numericValue } = updates.cell
            const key = payoffCellKey(aValue, bValue)
            if (numericValue === "" || numericValue === "-" || numericValue === "." || numericValue === "-.") {
              delete (node.data as Record<string, unknown>)[key]
            } else {
              const parsed = Number(numericValue)
              ;(node.data as Record<string, unknown>)[key] = Number.isNaN(parsed) ? numericValue : parsed
            }
            refreshFields.matrix = true
            editor.trigger("process")
          }
          void refreshPayoffMatrixNodeView(node, refreshFields).then(() => writeModel())
        },
        importPayoffMatrixValues: (nodeId: number, values: Record<string, string>) => {
          const node = findEditorNode(nodeId)
          if (!node || node.name !== PAYOFF_MATRIX_COMPONENT_NAME) {
            return
          }
          const data = node.data as Record<string, unknown>
          Object.keys(values).forEach(key => {
            const raw = values[key]
            if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
              delete data[key]
            } else {
              const parsed = Number(raw)
              data[key] = Number.isNaN(parsed) ? raw : parsed
            }
          })
          editor.trigger("process")
          void refreshPayoffMatrixNodeView(node, { matrix: true }).then(() => writeModel())
        },
        updateTable: (nodeId: number, updates) => {
          const node = findEditorNode(nodeId)
          if (!node || node.name !== NEW_TABLE_COMPONENT_NAME) {
            return
          }
          const refreshFields: { table?: boolean; expose?: boolean } = {}
          if (updates.expose !== undefined) {
            node.data.expose = updates.expose
            refreshFields.expose = true
            void refreshTableNodeView(node, refreshFields).then(() => writeModel())
            return
          }

          if (updates.rowName !== undefined) {
            const { index, name } = updates.rowName
            const rows = [...readEditorTableRows(node)]
            if (index >= 0 && index < rows.length) {
              rows[index] = name
              node.data[TABLE_ROWS_KEY] = rows
              refreshFields.table = true
              editor.trigger("process")
            }
          }

          if (updates.columnName !== undefined && !isEditorTableColumnsLocked(node)) {
            const { index, name } = updates.columnName
            const columns = [...readEditorTableColumns(node)]
            if (index >= 0 && index < columns.length) {
              columns[index] = name
              node.data[TABLE_COLUMNS_KEY] = columns
              refreshFields.table = true
              editor.trigger("process")
            }
          }

          if (updates.cell !== undefined) {
            const { rowIndex, columnIndex, value } = updates.cell
            const cells = readEditorTableCells(node)
            if (cells[rowIndex]) {
              const trimmed = value.trim()
              const parsed = trimmed === "" ? null : Number(trimmed)
              cells[rowIndex][columnIndex] =
                parsed === null || Number.isNaN(parsed) ? null : parsed
              writeEditorTableCells(node, cells)
              refreshFields.table = true
              editor.trigger("process")
            }
          }

          if (refreshFields.table) {
            void refreshTableNodeView(node, refreshFields).then(() => writeModel())
          }
        },
        importTableData: (nodeId: number, imported: TableSpreadsheetData) => {
          const node = findEditorNode(nodeId)
          if (!node || node.name !== NEW_TABLE_COMPONENT_NAME) {
            return
          }
          const locked = isEditorTableColumnsLocked(node) ? readEditorTableColumns(node) : null
          const merged = mergeImportedTable(imported, locked)
          node.data[TABLE_ROWS_KEY] = merged.rows
          node.data[TABLE_COLUMNS_KEY] = merged.columns
          node.data[TABLE_CELLS_KEY] = merged.cells
          editor.trigger("process")
          void refreshTableNodeView(node, { table: true }).then(() => writeModel())
        },
      })
    }

    // Rete's node-id counter (`Node.latestId`) is static on the class, so it
    // persists across editor instances for the lifetime of the browser tab.
    // Without resetting it, opening a second project continues numbering from
    // wherever the previous one left off (e.g. a fresh model starting at #41).
    // Reset it per load so each project's ids derive solely from that project:
    //   • empty project → first added node is #1
    //   • saved project → `fromJSON` bumps the counter to the project's own
    //                      highest id via `Node.latestId = Math.max(...)`, so
    //                      new nodes start at (highest id + 1).
    Node.resetId()

    if (initialModel !== null) {
      // Never pass `state.project.model` into the editor by reference: Rete mutates `node.data`
      // in place, which would mutate React state and break `isEqual` dirty detection (Save stays disabled).
      const workingCopy = cloneModelData(initialModel as Data)
      stripTransientTableNodeData(workingCopy)
      const updated = updateForBackwardsCompatibility(workingCopy, editor.components)
      const loadedComments = extractModelComments(updated)
      modelCommentsRef.current = loadedComments
      setModelComments(loadedComments)
      // Skip a pre-load `setModel(...)`: we'd just be writing the *same*
      // graph React already has, then overwriting it again once `fromJSON`
      // resolves with the editor's canonical snapshot. The intermediate
      // render is wasted work — it kicks the legend / minimap / detail
      // panel for nothing while the heavy editor mount is in progress.
      const graphForEditor = cloneModelData(updated as Data)
      // -----------------------------------------------------------------
      // Batch React node re-renders triggered by `connectioncreated`
      //
      // The Rete react render plugin installs this listener:
      //
      //     editor.on('connectioncreated connectionremoved', conn => {
      //       conn.output.node.update();
      //       conn.input.node.update();
      //     });
      //
      // Each `node.update()` is a synchronous `ReactDOM.render` of the
      // whole NodeComponent (with its 7-way control type filtering,
      // breadcrumb, inputs/outputs, etc.). During `fromJSON`'s connection
      // phase, with M connections among N nodes, this fires **2M**
      // synchronous re-renders, plus a re-render per shared endpoint.
      // For a model with ~50 connections this saturates the main thread
      // for >1s — that's the pause where nodes are visible but
      // connections (and the queued `updateconnection` RAFs that draw
      // them) are stuck behind the React work queue.
      //
      // Fix: intercept `node.update` for every node as it gets rendered,
      // and during `fromJSON` collect the requested updates instead of
      // running them. After `fromJSON` resolves we flush a single update
      // per touched node (deduped), which is enough for React to reflect
      // the loaded connections in input control visibility.
      // -----------------------------------------------------------------
      let suppressNodeUpdates = false
      const nodesPendingUpdate = new Set<Node>()
      editor.on('rendernode', ({ node }) => {
        const originalUpdate = (node as Node & { update?: () => Promise<void> }).update
        if (typeof originalUpdate !== 'function') return
        ;(node as Node & { update?: () => Promise<void> }).update = function () {
          if (suppressNodeUpdates) {
            nodesPendingUpdate.add(node)
            return Promise.resolve()
          }
          return originalUpdate.call(node)
        }
      })
      suppressNodeUpdates = true
      // Mirror `suppressNodeUpdates`: while `fromJSON` replays connections,
      // also suppress the `connectioncreated`/`connectionremoved` listeners
      // installed by the categorical components, which would otherwise
      // re-walk the upstream chain (O(N*M*D)) to "discover" labels we
      // already have under `node.data.catMapLabels` / `splitLabels` /
      // `mergedLabels`. The same suppression covers `connectionremoved`
      // events fired during a teardown step inside the React render plugin.
      suspendCategoricalChainSeeding()
      let chainSeedSuppressionReleased = false
      const releaseChainSeedSuppression = () => {
        if (chainSeedSuppressionReleased) return
        chainSeedSuppressionReleased = true
        resumeCategoricalChainSeeding()
      }
      editor.fromJSON(graphForEditor).then(() => {
        releaseChainSeedSuppression()
        const snapshot = cloneModelData(editor.toJSON() as Data) as DataWithComments
        snapshot.modelComments = modelCommentsRef.current
        // React 17 doesn't auto-batch setState calls fired from a Promise
        // continuation — each would trigger its own render of <ModelView>
        // and ripple through every subscriber (sidebar, legend, layer
        // palette, analysis panel, etc.). Batching collapses these four
        // post-load writes into a single render pass.
        unstable_batchedUpdates(() => {
          setModel(snapshot)
          setModelHistory([snapshot])
          setHistoryIndex(0)
          setInitialModelLoaded(true)
        })
        notifyInitialLoadComplete()
        // Flush the deferred React updates: one render per node that had
        // a connection created, instead of two per connection.
        suppressNodeUpdates = false
        const pending = Array.from(nodesPendingUpdate)
        nodesPendingUpdate.clear()
        pending.forEach(node => {
          const update = (node as Node & { update?: () => Promise<void> }).update
          if (typeof update === 'function') update.call(node)
        })
        scheduleAllDynamicLabelOutputRefreshes(editor)
        // After React commits the flushed renders, force every connection
        // to recompute its path against the laid-out DOM. The
        // `rendersocket` → `requestAnimationFrame(view.update)` plumbing
        // already does this incrementally, but a single sweep on the next
        // frame guarantees nothing is left stranded at `(0,0,0,0)` if any
        // ref callback raced the layout pass.
        window.requestAnimationFrame(() => {
          editor.nodes.forEach(node => editor.view?.updateConnections({ node }))
        })
      }).then(addNodeSyncListeners).then(addWriteModelListener).then(notifyEditorReady)
        .then(releaseChainSeedSuppression, releaseChainSeedSuppression)
    } else {
      addNodeSyncListeners()
      addWriteModelListener()
      notifyEditorReady()
      modelCommentsRef.current = []
      const snapshot = cloneModelData(editor.toJSON() as Data) as DataWithComments
      snapshot.modelComments = []
      unstable_batchedUpdates(() => {
        setModelComments([])
        setModelHistory([snapshot])
        setHistoryIndex(0)
        setInitialModelLoaded(true)
      })
      notifyInitialLoadComplete()
    }

    setEngine(engine)
    setEditor(editor)

    return () => {
      writeModelRef.current = undefined
      writeModel()
      detachConnectionMenu()
      // Do not flushSync: this cleanup is a React lifecycle, and flushSync
      // cannot run while React is rendering or committing. The portal
      // unmounts on the next commit; editor.destroy() removes the host
      // with the canvas.
      setCommentPortalHost(null)
      if (commentHost.parentNode) {
        commentHost.parentNode.removeChild(commentHost)
      }
      editor.destroy()
      engine.destroy()
      setEditor(undefined)
      setEngine(undefined)
      setInitialModelLoaded(false)
      didInitialFitRef.current = false
      didNotifyInitialLoadRef.current = false
    }
  }, [ref])

  /**
   * Toolbox → canvas drag-and-drop. Listens on the Rete canvas element for HTML5 drag
   * events with our custom mime types, then creates a node or sticky note at the drop
   * position in editor coordinates (rect-relative, divided by current view transform/zoom).
   */
  React.useEffect(() => {
    if (!editor) return
    const canvasEl = ref.current
    if (!canvasEl) return

    const hasMime = (e: DragEvent, mime: string) => {
      const types = e.dataTransfer?.types
      if (!types) return false
      for (let i = 0; i < types.length; i += 1) {
        if (types[i] === mime) return true
      }
      return false
    }

    const isToolboxCanvasDrag = (e: DragEvent) =>
      hasMime(e, COMPONENT_MIME) || hasMime(e, NOTE_MIME)

    const editorPointFromDrop = (e: DragEvent) => {
      const rect = canvasEl.getBoundingClientRect()
      const t = editor.view.area.transform
      return {
        x: (e.clientX - rect.left - t.x) / t.k,
        y: (e.clientY - rect.top - t.y) / t.k,
      }
    }

    const addDroppedNote = (e: DragEvent) => {
      const { x, y } = editorPointFromDrop(e)
      const comment: ModelComment = normalizeComment({
        id:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `c-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        x: x - DROPPED_NOTE_WIDTH / 2,
        y: y - DROPPED_NOTE_HEIGHT / 2,
        width: DROPPED_NOTE_WIDTH,
        height: DROPPED_NOTE_HEIGHT,
        colorId: DEFAULT_COMMENT_COLOR,
      })
      const next = [...modelCommentsRef.current, comment]
      modelCommentsRef.current = next
      setModelComments(next)
      writeModelRef.current?.()
    }

    const handleDragOver = (e: DragEvent) => {
      if (!isToolboxCanvasDrag(e)) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"
      canvasEl.classList.add("model-editor-canvas--drop-target")
    }
    const handleDragLeave = (e: DragEvent) => {
      // Only clear when leaving the canvas area, not when crossing child elements.
      const related = e.relatedTarget as globalThis.Node | null
      if (related && canvasEl.contains(related)) return
      canvasEl.classList.remove("model-editor-canvas--drop-target")
    }
    const handleDrop = async (e: DragEvent) => {
      canvasEl.classList.remove("model-editor-canvas--drop-target")
      e.preventDefault()

      if (hasMime(e, NOTE_MIME) && e.dataTransfer?.getData(NOTE_MIME)) {
        addDroppedNote(e)
        return
      }

      const name = e.dataTransfer?.getData(COMPONENT_MIME)
      if (!name) return
      let component
      try {
        component = editor.getComponent(name)
      } catch (_err) {
        return
      }
      if (!component) return
      const { x, y } = editorPointFromDrop(e)
      try {
        const node = await component.createNode()
        node.position = [x, y]
        editor.addNode(node)
      } catch (err) {
        console.warn("Failed to add dropped component", err)
      }
    }

    canvasEl.addEventListener("dragover", handleDragOver)
    canvasEl.addEventListener("dragleave", handleDragLeave)
    canvasEl.addEventListener("drop", handleDrop)

    return () => {
      canvasEl.removeEventListener("dragover", handleDragOver)
      canvasEl.removeEventListener("dragleave", handleDragLeave)
      canvasEl.removeEventListener("drop", handleDrop)
      canvasEl.classList.remove("model-editor-canvas--drop-target")
    }
  }, [editor])

  /**
   * Connected-chain highlight. When a node is selected, walk both directions
   * — upstream through inputs[*].connections[*].output.node AND downstream
   * through outputs[*].connections[*].input.node — and:
   *   - publish focusedId and chainIds into the nodeFocusStore — each
   *     `NodeComponent` subscribes and pulls one of the modifier classes
   *     (`node--focus`, `node--chain`, `node--faded`) into its
   *     React-rendered `className`, so the highlight survives reconciliation.
   *   - mutate connection DOM directly (`connection--in-chain` /
   *     `connection--faded`) — connections are rendered by Rete core, not
   *     React, so direct mutation is safe.
   *
   * Selection is taken from Rete's existing `nodeselected` event so this
   * cooperates with normal click-to-select / drag behavior. Clicking the
   * canvas background clears the focus.
   *
   * NOTE: Rete v1 has no `.off()` for unregistering listeners, so we attach
   * them in a `[editor]`-only effect (one set of listeners per editor
   * lifetime) and refer to the highlight implementation through a ref. A
   * second effect (further down) calls the ref whenever `focusedNodeId`
   * changes.
   */
  const applyHighlightRef = React.useRef<(() => void) | null>(null)
  React.useEffect(() => {
    if (!editor) return
    const canvasEl = ref.current
    if (!canvasEl) return

    const CONN_CLASS_IN_CHAIN = "connection--in-chain"
    const CONN_CLASS_FADED = "connection--faded"

    const clearConnectionHighlights = () => {
      editor.view.connections.forEach((cv: any) => {
        cv.el?.classList?.remove(CONN_CLASS_IN_CHAIN, CONN_CLASS_FADED)
      })
    }

    /**
     * Strictly directional BFS from `focused`:
     *   - upstream walk only follows inputs[*].connections[*].output.node
     *   - downstream walk only follows outputs[*].connections[*].input.node
     *
     * The two walks must NOT be combined into one BFS that follows both
     * directions from every node — that would let an upstream ancestor's
     * other downstream consumers (siblings of `focused`) leak into the chain
     * via the ancestor's outputs, and vice versa. We want the focused node's
     * lineage only: its ancestors and its descendants, not their siblings.
     */
    const computeChain = (focused: any): Set<number> => {
      const chain = new Set<number>([focused.id])

      const upStack: any[] = [focused]
      while (upStack.length > 0) {
        const n = upStack.pop()
        n?.inputs?.forEach((input: any) => {
          input?.connections?.forEach((conn: any) => {
            const up = conn?.output?.node
            if (up && !chain.has(up.id)) {
              chain.add(up.id)
              upStack.push(up)
            }
          })
        })
      }

      const downStack: any[] = [focused]
      while (downStack.length > 0) {
        const n = downStack.pop()
        n?.outputs?.forEach((output: any) => {
          output?.connections?.forEach((conn: any) => {
            const down = conn?.input?.node
            if (down && !chain.has(down.id)) {
              chain.add(down.id)
              downStack.push(down)
            }
          })
        })
      }

      return chain
    }

    const applyHighlight = () => {
      const id = focusedNodeIdRef.current
      if (id == null) {
        setNodeFocusState({ focusedId: null, chainIds: new Set<number>() })
        clearConnectionHighlights()
        return
      }
      const focused = editor.nodes.find(n => n.id === id)
      if (!focused) {
        setNodeFocusState({ focusedId: null, chainIds: new Set<number>() })
        clearConnectionHighlights()
        return
      }
      const chainIds = computeChain(focused)
      setNodeFocusState({ focusedId: id, chainIds })

      editor.view.connections.forEach((cv: any, connection: any) => {
        const el = cv?.el
        if (!el) return
        el.classList.remove(CONN_CLASS_IN_CHAIN, CONN_CLASS_FADED)
        const upId = connection?.output?.node?.id
        const downId = connection?.input?.node?.id
        const inChain =
          upId != null && downId != null && chainIds.has(upId) && chainIds.has(downId)
        el.classList.add(inChain ? CONN_CLASS_IN_CHAIN : CONN_CLASS_FADED)
      })
    }

    applyHighlightRef.current = applyHighlight
    applyHighlight()

    /**
     * Focus mode is a deliberate gesture: F+click on a node. Plain clicks
     * (normal Rete selection — drag, edit name, etc.) must NOT enter focus
     * mode, and must clear any existing focus so the user can return to the
     * default view without an extra background click.
     *   - F held → focus that node's connected chain.
     *   - F not held → clear focus (no chain highlight); Rete still
     *     selects the node normally.
     */
    const onNodeSelected = (node: any) => {
      if (fKeyHeldRef.current) {
        setFocusedNodeId(node?.id ?? null)
      } else if (focusedNodeIdRef.current != null) {
        setFocusedNodeId(null)
      }
    }
    const onNodeRemoved = (node: any) => {
      if (focusedNodeIdRef.current === node?.id) setFocusedNodeId(null)
    }
    const onConnectionChange = () => {
      // Re-apply against the latest graph topology.
      applyHighlightRef.current?.()
    }

    editor.on("nodeselected", onNodeSelected)
    editor.on("noderemoved", onNodeRemoved)
    editor.on("connectioncreated", onConnectionChange)
    editor.on("connectionremoved", onConnectionChange)

    /**
     * Clear focus when the user clicks on the canvas background — i.e. the
     * pointerdown target isn't inside a node, socket, connection, control,
     * comment box, or the legend/sidebar. Uses capture phase so we evaluate
     * before Rete starts a pan, but we never call preventDefault — panning
     * still works.
     */
    const onPointerDownClear = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (e.ctrlKey || e.metaKey) return
      const target = e.target as HTMLElement | null
      if (!target) return
      if (
        target.closest(
          ".node, .socket, .connection, .control, .model-comment-box, .model-editor-legend, .model-editor-legend-toggle, .model-editor-chat, [class*='minimap']"
        )
      )
        return
      if (focusedNodeIdRef.current != null) setFocusedNodeId(null)
    }
    canvasEl.addEventListener("pointerdown", onPointerDownClear, true)

    return () => {
      canvasEl.removeEventListener("pointerdown", onPointerDownClear, true)
      setNodeFocusState({ focusedId: null, chainIds: new Set<number>() })
      clearConnectionHighlights()
      applyHighlightRef.current = null
    }
  }, [editor])

  // Re-apply highlight whenever the focus selection changes. Listener
  // subscription lives in the `[editor]`-only effect above so Rete's
  // off-less emitter doesn't accumulate duplicates across renders.
  React.useEffect(() => {
    applyHighlightRef.current?.()
  }, [focusedNodeId])

  React.useEffect(() => {

    // TOGGLES MANUAL/AUTOMATIC RECALCULATION
    // Reprocess only on structural graph changes and debounce properly for large models.
    const debouncedProcess = debounce(async () => {
      if (autoProcessing && engine && editor) {
        setProcessing(true)
        await engine.abort()
        await engine.process(editor.toJSON())
        writeModelRef.current?.()
        setProcessing(false)
      }
    }, 250)

    editor?.on(
      ["noderemoved", "connectioncreated", "connectionremoved"],
      debouncedProcess
    )

    return () => {
      debouncedProcess.cancel()
    }
  }, [editor, engine, autoProcessing])

  const geographyRef = React.useRef({ extent, zoom, mask, maskLayer, maskCQL, maskShape, externalLink })
  geographyRef.current = { extent, zoom, mask, maskLayer, maskCQL, maskShape, externalLink }
  const registeredComponentsRef = React.useRef(registeredComponents)
  registeredComponentsRef.current = registeredComponents
  const geographyKey = JSON.stringify([extent, zoom, mask, maskLayer, maskCQL, maskShape ?? null, externalLink ?? null])
  const appliedGeographyKeyRef = React.useRef(geographyKey)

  // The editor is built once. When a public viewer switches area, push the new
  // extent into the live components and drop grids cached for the previous one
  // before the next process run.
  React.useEffect(() => {
    if (appliedGeographyKeyRef.current === geographyKey) return
    if (registeredComponentsRef.current.length === 0) return
    appliedGeographyKeyRef.current = geographyKey
    const props = geographyRef.current
    registeredComponentsRef.current.forEach(component => applyProjectProperties(component, props))
  }, [geographyKey, registeredComponents])

  React.useEffect(() => {

    // MANUAL RECALCULATION TRIGGER

    const processManual = async () => {

      if (engine && editor && process) {
        setProcessing(true)
        await engine.abort()
        await engine.process(editor.toJSON())
        writeModelRef.current?.()
        setProcessing(false)
        setProcess(false)
      }
    }

    processManual()

  }, [process, editor, engine])

  /**
   * Fit-to-screen on initial load. Runs once after the editor exists, the
   * initial model JSON has been imported, and the canvas is visible (so the
   * container has real width/height to fit against). We reuse the existing
   * `model:zoomToFit` listener — single source of truth for the framing math.
   */
  React.useEffect(() => {
    if (!editor || !visible || !initialModelLoaded) return
    if (didInitialFitRef.current) return
    if (editor.nodes.length === 0) {
      // No nodes to fit to — mark as done so we don't keep re-checking.
      didInitialFitRef.current = true
      return
    }

    // Defer two animation frames before fitting:
    //   1. lets Rete commit positions / sizes from fromJSON
    //   2. lets the browser actually paint the .node DOM elements so the fit
    //      logic can measure their real height instead of falling back to
    //      estimates (which underestimate tall nodes and push them low).
    let frame2: number | null = null
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        if (didInitialFitRef.current) return
        didInitialFitRef.current = true
        window.dispatchEvent(new Event("model:zoomToFit"))
      })
    })

    return () => {
      window.cancelAnimationFrame(frame1)
      if (frame2 !== null) window.cancelAnimationFrame(frame2)
    }
  }, [editor, visible, initialModelLoaded])

  React.useEffect(() => {
    if (!editor || !visible) return

    const refreshLayout = () => {
      const view: any = editor.view as any
      if (typeof view?.resize === "function") {
        view.resize()
      }
      editor.view.area.update()
      editor.nodes.forEach(node => editor.view?.updateConnections({ node }))
      setTransform(editor.view.area.transform)
    }

    // Reflow immediately and again shortly after mount/visibility flip to
    // handle hidden->visible layout changes in flex containers.
    refreshLayout()
    const rafId = window.requestAnimationFrame(refreshLayout)
    const timeoutId = window.setTimeout(refreshLayout, 120)

    const onResize = debounce(refreshLayout, 80)
    window.addEventListener("resize", onResize)

    // Observe the canvas container directly so Rete reflows whenever the
    // editor area itself changes size — e.g. opening / closing the AEGIS
    // sidebar, the layer palette, devtools, or any other layout shift that
    // doesn't fire a window resize event.
    let resizeObserver: ResizeObserver | null = null
    const canvasEl = ref.current
    if (canvasEl && typeof window.ResizeObserver !== "undefined") {
      resizeObserver = new window.ResizeObserver(() => onResize())
      resizeObserver.observe(canvasEl)
      if (canvasEl.parentElement) resizeObserver.observe(canvasEl.parentElement)
    }

    return () => {
      window.cancelAnimationFrame(rafId)
      window.clearTimeout(timeoutId)
      window.removeEventListener("resize", onResize)
      onResize.cancel()
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [editor, visible])

  const canUndo = historyIndex > 0
  const canRedo = historyIndex >= 0 && historyIndex < modelHistory.length - 1

  const applyHistoryAt = (index: number) => {
    if (!editor) return
    const snapshot = modelHistory[index] as DataWithComments | undefined
    if (!snapshot) return
    setCommentMenuId(null)
    const comments = extractModelComments(snapshot)
    modelCommentsRef.current = comments
    setModelComments(comments)
    isApplyingHistoryRef.current = true
    // Same chain-seed suppression as the initial-load `fromJSON` — undo/redo
    // replays connections in bulk and would otherwise hit the same O(N*M*D)
    // categorical re-seeding cost.
    suspendCategoricalChainSeeding()
    let released = false
    const release = () => {
      if (released) return
      released = true
      resumeCategoricalChainSeeding()
    }
    editor.fromJSON(cloneModelData(snapshot as Data)).then(
      () => {
        release()
        setModel(cloneModelData(snapshot))
        setTransform(editor.view.area.transform)
        isApplyingHistoryRef.current = false
      },
      () => {
        release()
        isApplyingHistoryRef.current = false
      }
    )
  }

  const handleUndo = () => {
    if (!canUndo) return
    const targetIndex = historyIndex - 1
    setHistoryIndex(targetIndex)
    applyHistoryAt(targetIndex)
  }

  const handleRedo = () => {
    if (!canRedo) return
    const targetIndex = historyIndex + 1
    setHistoryIndex(targetIndex)
    applyHistoryAt(targetIndex)
  }

  React.useEffect(() => {
    const undoListener = () => handleUndo()
    const redoListener = () => handleRedo()
    const zoomToFitListener = () => {
      if (!editor || !ref.current) return

      const nodes = editor.nodes
      if (!nodes.length) return

      // Frame against the canvas element (where Rete actually renders), NOT
      // the outer model-editor div — the latter is wider due to the legend
      // and the AEGIS/Toolbox sidebar, which would skew horizontal centering.
      const canvasEl = ref.current

      // Build the bounding box from the actual rendered node DOM elements
      // when available. Fixed estimates (e.g. 260×160) under-count tall
      // nodes, which shifts the computed bbox center upward and causes the
      // model to render visually low in the viewport. Falling back to
      // estimates only when no DOM is mounted yet (e.g. very first frame).
      const FALLBACK_WIDTH = 260
      const FALLBACK_HEIGHT = 200

      const canvasRect = canvasEl.getBoundingClientRect()
      const t = editor.view.area.transform

      const nodeElById = new Map<number, HTMLElement>()
      canvasEl.querySelectorAll<HTMLElement>(".node").forEach(el => {
        const idAttr = el.dataset?.nodeId ?? el.getAttribute("data-node-id")
        // Rete v1 doesn't reliably stamp data-node-id; fall back to listing
        // .node elements and pairing them with editor.nodes by index later.
        if (idAttr) {
          const idNum = Number(idAttr)
          if (Number.isFinite(idNum)) nodeElById.set(idNum, el)
        }
      })

      // If we couldn't id-map, fall back to traversal order — Rete renders
      // nodes in the same order they're enumerated by `editor.nodes`.
      const nodeEls =
        nodeElById.size > 0
          ? null
          : Array.from(canvasEl.querySelectorAll<HTMLElement>(".node"))

      let minX = Infinity
      let minY = Infinity
      let maxX = -Infinity
      let maxY = -Infinity

      nodes.forEach((node, idx) => {
        if (!node.position) return
        const nodeX = node.position[0]
        const nodeY = node.position[1]

        const el =
          (nodeElById.size > 0 ? nodeElById.get(node.id) : nodeEls?.[idx]) ?? null

        let widthEditor = FALLBACK_WIDTH
        let heightEditor = FALLBACK_HEIGHT
        if (el) {
          const r = el.getBoundingClientRect()
          // Convert rendered px → editor units using the current zoom.
          if (t.k > 0 && r.width > 0 && r.height > 0) {
            widthEditor = r.width / t.k
            heightEditor = r.height / t.k
          }
        }

        minX = Math.min(minX, nodeX)
        minY = Math.min(minY, nodeY)
        maxX = Math.max(maxX, nodeX + widthEditor)
        maxY = Math.max(maxY, nodeY + heightEditor)
      })

      if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
        return
      }

      const bboxWidth = maxX - minX
      const bboxHeight = maxY - minY
      if (bboxWidth <= 0 || bboxHeight <= 0) return

      const margin = 40
      // Use the canvas's own dimensions, not the outer container's, so the
      // model is centered in the area where it actually renders.
      const canvasWidth = canvasRect.width || canvasEl.clientWidth
      const canvasHeight = canvasRect.height || canvasEl.clientHeight
      const availableWidth = canvasWidth - margin * 2
      const availableHeight = canvasHeight - margin * 2
      if (availableWidth <= 0 || availableHeight <= 0) return

      let scale = Math.min(availableWidth / bboxWidth, availableHeight / bboxHeight)
      // Zoom out slightly so nodes are not right up against the edges.
      scale *= 0.6
      // Cap the zoom-in so small graphs (single node, tiny clusters) don't
      // get blown up to a huge scale that wastes the canvas and hides the
      // surrounding workspace. `1` = native size — same scale a user sees
      // when first opening a brand new project.
      const MAX_FIT_SCALE = 1
      if (scale > MAX_FIT_SCALE) scale = MAX_FIT_SCALE

      const bboxCenterX = minX + bboxWidth / 2
      const bboxCenterY = minY + bboxHeight / 2
      const canvasCenterX = canvasWidth / 2
      const canvasCenterY = canvasHeight / 2

      const x = canvasCenterX - bboxCenterX * scale
      const y = canvasCenterY - bboxCenterY * scale

      editor.view.area.transform = { k: scale, x, y }
      editor.view.area.update()
      setTransform(editor.view.area.transform)
    }

    window.addEventListener('model:undo', undoListener)
    window.addEventListener('model:redo', redoListener)
    window.addEventListener('model:zoomToFit', zoomToFitListener)
    return () => {
      window.removeEventListener('model:undo', undoListener)
      window.removeEventListener('model:redo', redoListener)
      window.removeEventListener('model:zoomToFit', zoomToFitListener)
    }
  }, [editor, modelHistory, historyIndex])

  const patchComment = (id: string, patch: Partial<ModelComment>) => {
    const next = modelCommentsRef.current.map(c =>
      c.id === id ? normalizeComment({ ...c, ...patch }) : c
    )
    modelCommentsRef.current = next
    setModelComments(next)
    writeModelRef.current?.()
  }

  const deleteCommentById = (id: string) => {
    const next = modelCommentsRef.current.filter(c => c.id !== id)
    modelCommentsRef.current = next
    setModelComments(next)
    setCommentMenuId(null)
    writeModelRef.current?.()
  }

  const setCommentRectLocal = (id: string, x: number, y: number, width: number, height: number) => {
    const next = modelCommentsRef.current.map(c =>
      c.id === id ? normalizeComment({ ...c, x, y, width, height }) : c
    )
    modelCommentsRef.current = next
    setModelComments(next)
  }

  const growCommentWidthToFitText = (id: string, textarea: HTMLTextAreaElement) => {
    const current = modelCommentsRef.current.find(c => c.id === id)
    if (!current) return
    const cs = window.getComputedStyle(textarea)
    const lines = textarea.value.split("\n")
    const longestLine = lines.reduce((a, b) => (a.length >= b.length ? a : b), "")

    if (!textMeasureCanvasRef.current) {
      textMeasureCanvasRef.current = document.createElement("canvas")
    }
    const ctx = textMeasureCanvasRef.current.getContext("2d")
    if (!ctx) return
    ctx.font = cs.font

    const textWidth = Math.ceil(ctx.measureText(longestLine).width)
    const textareaPaddingX =
      (parseFloat(cs.paddingLeft || "0") || 0) +
      (parseFloat(cs.paddingRight || "0") || 0)
    const textareaHorizontalChrome = textareaPaddingX + 8
    const textareaNeededWidth = textWidth + textareaHorizontalChrome
    const noteHorizontalChrome = 28 // textarea left/right offsets: 14px + 14px
    const neededNoteWidth = Math.ceil(textareaNeededWidth + noteHorizontalChrome)

    if (neededNoteWidth > current.width) {
      setCommentRectLocal(id, current.x, current.y, neededNoteWidth, current.height)
    }
  }

  const flushCommentTextFromDraft = () => {
    const id = commentMenuId
    if (!id) return
    const cur = modelCommentsRef.current.find(c => c.id === id)
    if (!cur) return
    // Preserve the user's exact input — including trailing newlines or
    // blank lines they typed for spacing. `.trim()` is only used to decide
    // whether the note is logically empty (in which case `text` is cleared
    // to `undefined`); it must not influence the value we persist.
    const draft = commentTextDraft
    const prev = cur.text ?? ""
    if (prev === draft) return
    patchComment(id, { text: draft.trim() ? draft : undefined })
  }

  const selectComment = (id: string | null) => {
    if (commentMenuId && commentMenuId !== id) {
      flushCommentTextFromDraft()
    }
    setCommentMenuId(id)
  }

  React.useEffect(() => {
    if (!commentMenuId) {
      setCommentTextDraft("")
      return
    }
    const c = modelComments.find(mc => mc.id === commentMenuId)
    setCommentTextDraft(c?.text ?? "")
  }, [commentMenuId])

  React.useEffect(() => {
    if (!commentMenuId) return
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target
      if (!(t instanceof Element)) return
      if (t.closest(".model-comment-box")) return
      flushCommentTextFromDraft()
      setCommentMenuId(null)
    }
    document.addEventListener("pointerdown", onDocPointerDown, true)
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true)
  }, [commentMenuId, commentTextDraft])

  React.useEffect(() => {
    if (!commentMenuId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        flushCommentTextFromDraft()
        setCommentMenuId(null)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [commentMenuId, commentTextDraft])

  React.useEffect(() => {
    if (!editor || !visible) return
    const container = containerRef.current
    const canvasEl = ref.current
    if (!container || !canvasEl) return

    const rectNormEditor = (
      r: DOMRect,
      t: Transform,
      clientStart: { x: number; y: number },
      clientEnd: { x: number; y: number }
    ) => {
      const sx = (clientStart.x - r.left - t.x) / t.k
      const sy = (clientStart.y - r.top - t.y) / t.k
      const ex = (clientEnd.x - r.left - t.x) / t.k
      const ey = (clientEnd.y - r.top - t.y) / t.k
      return {
        x: Math.min(sx, ex),
        y: Math.min(sy, ey),
        width: Math.abs(ex - sx),
        height: Math.abs(ey - sy),
      }
    }

    let dragStartClient: { x: number; y: number } | null = null

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (!(e.ctrlKey || e.metaKey)) return
      const el = e.target as HTMLElement | null
      if (el?.closest?.(".model-editor-legend, .model-editor-legend-toggle")) return
      if (el?.closest?.(".minimap, [class*='minimap']")) return
      if (el?.closest?.(".model-comment-box")) return

      dragStartClient = { x: e.clientX, y: e.clientY }
      const crect = canvasEl.getBoundingClientRect()
      const tr = editor.view.area.transform
      setCommentDrawPreview(rectNormEditor(crect, tr, dragStartClient, dragStartClient))
      e.preventDefault()
      e.stopPropagation()

      const move = (ev: PointerEvent) => {
        if (!dragStartClient) return
        if (!(ev.ctrlKey || ev.metaKey)) {
          dragStartClient = null
          setCommentDrawPreview(null)
          window.removeEventListener("pointermove", move)
          window.removeEventListener("pointerup", up)
          window.removeEventListener("pointercancel", up)
          return
        }
        const r = canvasEl.getBoundingClientRect()
        const tr = editor.view.area.transform
        setCommentDrawPreview(rectNormEditor(r, tr, dragStartClient, { x: ev.clientX, y: ev.clientY }))
      }

      const up = (ev: PointerEvent) => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", up)
        window.removeEventListener("pointercancel", up)

        if (!dragStartClient) return
        const start = dragStartClient
        dragStartClient = null
        setCommentDrawPreview(null)

        const r = canvasEl.getBoundingClientRect()
        const t = editor.view.area.transform
        const sx = (start.x - r.left - t.x) / t.k
        const sy = (start.y - r.top - t.y) / t.k
        const ex = (ev.clientX - r.left - t.x) / t.k
        const ey = (ev.clientY - r.top - t.y) / t.k

        const x = Math.min(sx, ex)
        const y = Math.min(sy, ey)
        let width = Math.abs(ex - sx)
        let height = Math.abs(ey - sy)

        const MIN_EDITOR = 8
        if (width < MIN_EDITOR && height < MIN_EDITOR) {
          return
        }
        width = Math.max(width, MIN_EDITOR)
        height = Math.max(height, MIN_EDITOR)

        const comment: ModelComment = normalizeComment({
          id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          x,
          y,
          width,
          height,
          colorId: DEFAULT_COMMENT_COLOR,
        })

        const next = [...modelCommentsRef.current, comment]
        modelCommentsRef.current = next
        setModelComments(next)
        writeModelRef.current?.()
      }

      window.addEventListener("pointermove", move)
      window.addEventListener("pointerup", up)
      window.addEventListener("pointercancel", up)
    }

    container.addEventListener("pointerdown", onPointerDown, true)

    return () => {
      container.removeEventListener("pointerdown", onPointerDown, true)
      dragStartClient = null
      setCommentDrawPreview(null)
    }
  }, [editor, visible])

  const publicExtentApiHolder = React.useRef(publicExtentApiRef)
  publicExtentApiHolder.current = publicExtentApiRef

  /**
   * Bridge for the AEGIS chat agent — exposes a tiny, stable surface
   * (add/remove by name/id) so the chat tab can mutate the graph without
   * importing Rete internals. New nodes are placed near the centre of the
   * current viewport in editor coordinates, with a small per-call offset so
   * repeated adds don't pile on top of each other.
   */
  const agentActions = React.useMemo<AgentActions | undefined>(() => {
    if (!editor) return undefined
    return {
      addComponent: async (name: string) => {
        let component: BaseComponent | undefined
        try {
          component = editor.getComponent(name) as BaseComponent | undefined
        } catch {
          component = undefined
        }
        component = component ?? resolveRegisteredComponent(registeredComponents, name)
        if (!component) {
          throw new Error(`Unknown component: "${name}".`)
        }

        const node = await component.createNode()
        const canvas = ref.current
        if (canvas) {
          const rect = canvas.getBoundingClientRect()
          const t = editor.view.area.transform
          const cx = (rect.width / 2 - t.x) / t.k
          const cy = (rect.height / 2 - t.y) / t.k
          const offset = (editor.nodes.length % 8) * 30
          node.position = [cx + offset, cy + offset]
        }
        // Stagger batched adds: when the agent emits a whole plan in one turn
        // the tool calls run back-to-back, so without a pause every node (and
        // its particle burst) would pop in at once. A short wait BEFORE each
        // add spaces them out so they cascade onto the canvas one by one. The
        // executor awaits this call, so any dependent connect/set runs only
        // after the node actually exists.
        await new Promise(resolve => setTimeout(resolve, AGENT_ADD_STAGGER_MS))
        // Flag the add as agent-initiated so the `nodecreated` listener
        // fires the particle burst (it's suppressed for user adds). Rete
        // dispatches the event synchronously, so resetting in `finally`
        // keeps the flag scoped to exactly this call.
        isAgentActionRef.current = true
        try {
          editor.addNode(node)
        } finally {
          isAgentActionRef.current = false
        }
        return { id: node.id, name: component.name }
      },
      removeComponent: (id: number) => {
        const node = editor.nodes.find(n => n.id === id || String(n.id) === String(id))
        if (!node) return false
        isAgentActionRef.current = true
        try {
          editor.removeNode(node)
        } finally {
          isAgentActionRef.current = false
        }
        return true
      },
      setComponentName: (id, name) => {
        const node = findNodeById(editor.nodes, id)
        if (!node) return { ok: false, error: `No node with id ${id}.` }
        // Trim so " " doesn't masquerade as a real label and stop the
        // placeholder fallback from kicking in. `node.data.name` is the
        // same slot `node_component.tsx`'s namebar input writes to, so
        // setting it here puts the agent and the user on the same path.
        node.data.name = name.trim()
        // Two-step refresh mirrors `node_component.tsx` `handleInputChange`:
        //   • `node.update()`             — re-render the React node so the
        //                                   new label appears immediately.
        //   • `editor.trigger("process")` — fires the `process` event the
        //                                   write-model listener listens for,
        //                                   so the rename gets persisted.
        void (node as Node & { update?: () => unknown }).update?.()
        editor.trigger("process")
        return { ok: true }
      },
      setControl: (id, controlName, value) => {
        const node = findNodeById(editor.nodes, id)
        if (!node) return { ok: false, error: `No node with id ${id}.` }

        const found = findNodeControl(node, controlName)
        if (!found) {
          const available = listNodeControlNames(node).join(", ") || "(none)"
          return {
            ok: false,
            error: `No control "${controlName}" on node #${id}. Available: ${available}.`,
          }
        }

        const result = applyControlValue(found.control, value, found.matchedAs)
        if (!result.ok) return result
        // Force a React re-render of the control itself. Without this,
        // controls that don't subscribe to the editor's `process` event
        // (TextControl, NumericConstantRowControl, BooleanControl,
        // DateControl) keep showing their old `value={getValue()}` even
        // though `node.data` has been updated underneath. Select-family
        // controls subscribe to `process` so they refresh via the
        // `editor.trigger("process")` call below, but `control.update()`
        // is harmless to call on top of that and is the canonical Rete
        // way to push a control re-render — same pattern used by
        // `refreshNumericConstantNodeView` already in this file.
        const controlWithUpdate = found.control as { update?: () => unknown }
        if (typeof controlWithUpdate.update === "function") {
          void controlWithUpdate.update()
        }
        // Refresh + persist. `process` covers everything the React control
        // components subscribe to (forceUpdate listeners on SelectControl /
        // CheckboxSelectControl), and triggers the writeModel listener too.
        // `node.update()` covers control types whose React component
        // doesn't subscribe to `process` (e.g. plain TextControl) — a
        // belt-and-braces re-render.
        void (node as Node & { update?: () => unknown }).update?.()
        editor.trigger("process")
        return { ok: true }
      },
      connect: (fromId, fromOutput, toId, toInput) => {
        const sourceNode = findNodeById(editor.nodes, fromId)
        if (!sourceNode) return { ok: false, error: `No node with id ${fromId}.` }
        const targetNode = findNodeById(editor.nodes, toId)
        if (!targetNode) return { ok: false, error: `No node with id ${toId}.` }

        const output = findOutput(sourceNode, fromOutput)
        if (!output) {
          return { ok: false, error: `Node #${fromId} has no output "${fromOutput}".` }
        }
        const input = findInput(targetNode, toInput)
        if (!input) {
          return { ok: false, error: `Node #${toId} has no input "${toInput}".` }
        }
        if (output.connections.some(c => c.input === input)) {
          return { ok: false, error: "Those sockets are already connected." }
        }
        try {
          editor.connect(output, input)
          return { ok: true }
        } catch (err) {
          // Rete throws for incompatible sockets or for inputs that
          // already have a connection and don't allow multiples.
          const msg = err instanceof Error ? err.message : String(err)
          return { ok: false, error: msg }
        }
      },
      disconnect: (fromId, fromOutput, toId, toInput) => {
        const sourceNode = findNodeById(editor.nodes, fromId)
        if (!sourceNode) return { ok: false, error: `No node with id ${fromId}.` }
        const targetNode = findNodeById(editor.nodes, toId)
        if (!targetNode) return { ok: false, error: `No node with id ${toId}.` }

        const output = findOutput(sourceNode, fromOutput)
        if (!output) {
          return { ok: false, error: `Node #${fromId} has no output "${fromOutput}".` }
        }
        const input = findInput(targetNode, toInput)
        if (!input) {
          return { ok: false, error: `Node #${toId} has no input "${toInput}".` }
        }
        const connection = output.connections.find(c => c.input === input)
        if (!connection) {
          return { ok: false, error: "Those sockets are not currently connected." }
        }
        editor.removeConnection(connection)
        return { ok: true }
      },
      focusComponent: (id) => {
        if (publicShareSettingsRef.current) {
          onFocusMapOutputRef.current?.(id)
          return
        }
        const node = findNodeById(editor.nodes, id)
        if (!node) return
        const canvas = ref.current
        if (!canvas) return
        const rect = canvas.getBoundingClientRect()
        const { k } = editor.view.area.transform
        const pos = node.position ?? [0, 0]
        // Node elements are laid out in editor-space units (the area
        // container applies the zoom transform on top), so their offset
        // dimensions live in the same coordinate space as node.position —
        // letting us centre on the node's middle. Fall back to a typical
        // node footprint if the element isn't mounted yet.
        let nodeW = 250
        let nodeH = 80
        const el = canvas.querySelector(`[data-node-id="${id}"]`) as HTMLElement | null
        if (el) {
          nodeW = el.offsetWidth || nodeW
          nodeH = el.offsetHeight || nodeH
        }
        const x = rect.width / 2 - (pos[0] + nodeW / 2) * k
        const y = rect.height / 2 - (pos[1] + nodeH / 2) * k
        editor.view.area.transform = { k, x, y }
        editor.view.area.update()
        setTransform(editor.view.area.transform)
      },
      autoLayout: () => {
        // Re-arrange only the nodes the agent added this turn, anchoring them
        // around the user's existing nodes. See `model_view_layout.ts`.
        autoLayoutNodes(editor, ref.current, agentAddedNodesRef.current)
        // Consume the batch so a later question-only turn doesn't shuffle them.
        agentAddedNodesRef.current = new Set()
      },
      requestProcess: () => {
        setProcessing(true)
        // Clear a run that is already queued so the effect below starts again
        // against the graph the agent just wrote.
        setProcess(false)
        window.setTimeout(() => setProcess(true), 0)
      },
      setLayerStyle: (id, style) => {
        const node = findNodeById(editor.nodes, id)
        if (!node) return { ok: false, error: `No node with id ${id}.` }
        if (node.name !== "Map layer") {
          return {
            ok: false,
            error: `Node #${id} is "${node.name}", not a Map layer. set_layer_style only styles Map layer nodes.`,
          }
        }
        if (!mutateOutputLayer) {
          return { ok: false, error: "Layer styling is not available in this editor." }
        }

        const current = getOutputLayerStyle?.(id) ?? null
        const updates: {
          fill?: fillType
          colors?: Array<[number, number, number, number]>
          colorByLabel?: Record<string, [number, number, number, number]>
        } = {}

        if (style.fill) {
          updates.fill = style.fill as fillType
        }

        if (style.falseColor || style.trueColor) {
          const existing = current?.colors ?? []
          const falseColor =
            style.falseColor ??
            (Array.isArray(existing[0]) ? existing[0] : [0, 0, 0, 0])
          const trueColor =
            style.trueColor ??
            (Array.isArray(existing[1]) ? existing[1] : [255, 255, 255, 1])
          updates.colors = [
            [falseColor[0], falseColor[1], falseColor[2], falseColor[3] ?? 1],
            [trueColor[0], trueColor[1], trueColor[2], trueColor[3] ?? 1],
          ]
        }

        if (style.labelColors && Object.keys(style.labelColors).length > 0) {
          const colorByLabel: Record<string, [number, number, number, number]> = {
            ...(current?.colorByLabel ?? {}),
          }
          for (const [label, color] of Object.entries(style.labelColors)) {
            colorByLabel[categoryGroupKeyForLabel(label)] = [
              color[0],
              color[1],
              color[2],
              color[3] ?? 1,
            ]
          }
          updates.colorByLabel = colorByLabel
        }

        if (Object.keys(updates).length === 0) {
          return { ok: false, error: "No style fields to apply." }
        }

        const ok = mutateOutputLayer(id, updates)
        if (!ok) {
          return {
            ok: false,
            error: `No map-view layer found for Map layer node #${id}.`,
          }
        }
        return { ok: true }
      },
      listPublicExtents: () => publicExtentApiHolder.current?.current?.list() ?? [],
      switchPublicExtent: (id) => publicExtentApiHolder.current?.current?.switchTo(id) ?? {
        ok: false,
        error: "Area switching is only available on a public project.",
      },
      setLayerDescription: (id, description) => {
        const node = findNodeById(editor.nodes, id)
        if (!node) return { ok: false, error: `No node with id ${id}.` }
        if (node.name !== "Map layer") {
          return {
            ok: false,
            error: `Node #${id} is "${node.name}", not a Map layer. set_layer_description only applies to Map layer nodes.`,
          }
        }
        if (!mutateOutputLayer) {
          return { ok: false, error: "Layer descriptions are not available in this editor." }
        }
        const saved = mutateOutputLayer(id, { comment: description })
        if (!saved) {
          return { ok: false, error: `No map-view layer found for Map layer node #${id}.` }
        }
        return { ok: true }
      },
      // Synchronous snapshot — `editor.toJSON()` already reflects current
      // control values, and passing `editor.nodes` lets the formatter
      // resolve live socket labels (e.g. "Deciduous Woodland (1)") rather
      // than raw output keys.
      getReadableModel: () =>
        projectModelToReadable(
          { model: editor.toJSON() as Data },
          { editorNodes: editor.nodes }
        ) + formatMapLayerDescriptions(listMapLayerDescriptions?.() ?? []),
      getMapOutputs: () =>
        mapLayerOutputsToReadable({ model: editor.toJSON() as Data }) +
        formatMapLayerDescriptions(listMapLayerDescriptions?.() ?? []),
      // Async because `componentsToReadable` constructs a temporary
      // instance of each component to read its default controls.
      getReadableComponents: () => componentsToReadable(
        registeredComponents.filter(component => isComponentPubliclyAvailable(component, publicShareSettingsRef.current))
      ),
      searchProjects: async (query?: string) => {
        const params = new URLSearchParams({ json: "true" })
        const q = (query ?? "").trim()
        if (q) params.set("q", q)
        const response = await fetch(`/teams/${teamId}/projects?${params.toString()}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
        if (!response.ok) {
          throw new Error(`Failed to search projects (${response.status})`)
        }
        const data = await response.json()
        const list = Array.isArray(data) ? data : []
        return list.map((item: any) => ({
          id: Number(item.id),
          name: String(item.name || "Untitled project"),
          updated_at: String(item.updated_at || ""),
          is_current: projectId !== undefined && Number(item.id) === projectId,
        }))
      },
      readProjectModel: async (otherProjectId: number) => {
        const response = await fetch(`/projects/${otherProjectId}?json=true`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
        if (response.status === 404) {
          return { ok: false as const, error: `No project with id ${otherProjectId}.` }
        }
        if (!response.ok) {
          return {
            ok: false as const,
            error: `Failed to load project #${otherProjectId} (${response.status}).`,
          }
        }
        const data = await response.json()
        const name = String(data?.name || "Untitled project")
        const source = data?.source && typeof data.source === "object" ? data.source : {}
        const model = (source as { model?: Data | null }).model ?? null
        let readable = projectModelToReadable({ model })
        const MAX_CHARS = 40000
        if (readable.length > MAX_CHARS) {
          readable =
            readable.slice(0, MAX_CHARS) +
            `\n…(truncated — model outline exceeded ${MAX_CHARS} characters)`
        }
        return {
          ok: true as const,
          id: Number(data?.id ?? otherProjectId),
          name,
          model: readable,
        }
      },
    }
  }, [editor, registeredComponents, getOutputLayerStyle, mutateOutputLayer, listMapLayerDescriptions, teamId, projectId, setProcess, setProcessing])

  React.useEffect(() => {
    onAgentActionsReady?.(agentActions)
  }, [agentActions, onAgentActionsReady])

  return (
    <div
      ref={containerRef}
      className={`model-editor bg-dark flex-grow-1 ${visible ? "d-flex flex-column" : "d-none"}`}
    >
      <div className="model-editor-main-row">
      {legendVisible && (
        <div className="model-editor-legend">
          <div className="model-editor-legend-title">Legend</div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-layer" />
            <span className="model-editor-legend-label">Categorical Grid</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-map" />
            <span className="model-editor-legend-label">Numeric Grid</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-set" />
            <span className="model-editor-legend-label">Boolean Grid</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-categorical-or-boolean" />
            <span className="model-editor-legend-label">Categorical / Boolean Grid</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-number" />
            <span className="model-editor-legend-label">Number</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-number-or-numeric" />
            <span className="model-editor-legend-label">Number / Numeric Grid</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-dataset" />
            <span className="model-editor-legend-label">Any</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-table" />
            <span className="model-editor-legend-label">Table</span>
          </div>
          <div className="model-editor-legend-item">
            <span className="model-editor-legend-swatch model-editor-legend-swatch-property" />
            <span className="model-editor-legend-label">Property</span>
          </div>
        </div>
      )}
      <button
        type="button"
        className="btn btn-sm btn-dark model-editor-legend-toggle"
        aria-label={legendVisible ? "Hide legend" : "Show legend"}
        aria-expanded={legendVisible}
        onClick={() => setLegendVisible(v => !v)}
      >
        <i className={`fas fa-chevron-${legendVisible ? "down" : "up"}`} />
      </button>
      <div className="model-editor-canvas-wrap">
        <div className="model-editor-canvas" ref={ref}></div>
      </div>
      <ModelAgentSidebar
        components={registeredComponents.filter(component => isComponentPubliclyAvailable(component, publicShareSettings))}
        permissions={permissions}
        teamId={teamId}
        projectId={projectId}
        groqConfigured={groqConfigured}
        agentConfigured={agentConfigured}
        agentProvider={agentProvider}
        agentModel={agentModel}
        agentProviderId={agentProviderId}
        agentModelId={agentModelId}
        actions={agentActions}
        visible={visible}
      />
      </div>
      {commentPortalHost &&
        createPortal(
          <>
            {modelComments.map(c => {
              const border = commentBorderColor(c.colorId)
              const fill = commentFillColor(c.colorId)
              const selected = commentMenuId === c.id
              // Show the full body and let the CSS line-clamp on
              // `.model-comment-box__excerpt` handle visual truncation. A
               // JS char-count slice (the previous behaviour) cuts text off
              // before the visual clamp would ever kick in — short multi-
              // line notes were ending in "…" after the first sentence even
              // though the note box had plenty of room. `.trim()` is used
              // only as a "is there anything to show?" predicate; the value
              // we render is the unmodified text so internal newlines and
              // spacing survive the round-trip.
              const preview = c.text?.trim() ? c.text : ""
              const resizeHandleDefs = [
                { key: "nw" as const, className: "is-nw" },
                { key: "ne" as const, className: "is-ne" },
                { key: "se" as const, className: "is-se" },
                { key: "sw" as const, className: "is-sw" },
              ]
              return (
                <div
                  key={c.id}
                  className={`model-comment-box${selected ? " model-comment-box--selected" : ""}`}
                  style={{
                    position: "absolute",
                    left: c.x,
                    top: c.y,
                    width: c.width,
                    height: c.height,
                    borderColor: border,
                    color: border,
                    // The CSS variable is the single source of truth for
                    // the note background — `.model-comment-box`'s SCSS
                    // reads `var(--model-note-fill, …)`. We also write
                    // `backgroundColor` directly so the inline style wins
                    // over the SCSS default for the unselected and hover
                    // states without relying on selector specificity.
                    backgroundColor: fill,
                    ["--model-note-color" as string]: border,
                    ["--model-note-fill" as string]: fill,
                    ["--model-note-font-size" as string]: `${c.fontSize ?? DEFAULT_COMMENT_FONT_SIZE}px`,
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={c.text?.trim() ? `Comment: ${c.text.trim().slice(0, 80)}` : "Empty comment"}
                  onPointerDown={e => {
                    if (e.ctrlKey || e.metaKey) return
                    const target = e.target as HTMLElement | null
                    if (
                      target?.closest(".model-comment-resize-handle") ||
                      target?.closest(".model-comment-inline-controls") ||
                      target?.closest(".model-comment-inline-textarea")
                    ) {
                      return
                    }
                    e.stopPropagation()
                    selectComment(c.id)
                    if (!editor) return
                    e.preventDefault()

                    const startX = e.clientX
                    const startY = e.clientY
                    const base = { x: c.x, y: c.y }
                    let moved = false

                    const move = (ev: PointerEvent) => {
                      const t = editor.view.area.transform
                      const dx = (ev.clientX - startX) / t.k
                      const dy = (ev.clientY - startY) / t.k
                      if (Math.abs(dx) > 0.25 || Math.abs(dy) > 0.25) moved = true
                      setCommentRectLocal(c.id, base.x + dx, base.y + dy, c.width, c.height)
                    }

                    const up = () => {
                      window.removeEventListener("pointermove", move)
                      window.removeEventListener("pointerup", up)
                      window.removeEventListener("pointercancel", up)
                      if (moved) writeModelRef.current?.({ movement: true })
                    }

                    window.addEventListener("pointermove", move)
                    window.addEventListener("pointerup", up)
                    window.addEventListener("pointercancel", up)
                  }}
                  onKeyDown={e => {
                    const target = e.target as HTMLElement | null
                    if (
                      target?.closest("textarea, input, [contenteditable='true']") ||
                      target?.tagName === "TEXTAREA" ||
                      target?.tagName === "INPUT"
                    ) {
                      return
                    }
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      selectComment(c.id)
                    }
                  }}
                >
                  {selected ? (
                    <>
                      <div className="model-comment-inline-controls" onPointerDown={e => e.stopPropagation()}>
                        <div className="model-comment-inline-colors">
                          {COMMENT_COLOR_PRESETS.map(preset => {
                            const currentId = (c.colorId as CommentColorId | undefined) ?? DEFAULT_COMMENT_COLOR
                            const selectedColor = currentId === preset.id
                            return (
                              <button
                                key={preset.id}
                                type="button"
                                className={`model-comment-inline-color${selectedColor ? " is-active" : ""}`}
                                style={{ backgroundColor: preset.border }}
                                aria-label={`${preset.label} note colour`}
                                aria-pressed={selectedColor}
                                onPointerDown={e => e.stopPropagation()}
                                onClick={() => patchComment(c.id, { colorId: preset.id })}
                              />
                            )
                          })}
                        </div>
                        <label className="model-comment-inline-fontsize" aria-label="Font size">
                          <span>A</span>
                          <input
                            type="range"
                            min={MIN_COMMENT_FONT_SIZE}
                            max={MAX_COMMENT_FONT_SIZE}
                            step={1}
                            value={Math.round(c.fontSize ?? DEFAULT_COMMENT_FONT_SIZE)}
                            onPointerDown={e => e.stopPropagation()}
                            onChange={e => patchComment(c.id, { fontSize: Number(e.target.value) })}
                          />
                        </label>
                        <button
                          type="button"
                          className="model-comment-inline-delete"
                          onPointerDown={e => e.stopPropagation()}
                          onClick={() => deleteCommentById(c.id)}
                          aria-label="Delete note"
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      </div>
                      <textarea
                        className="model-comment-inline-textarea"
                        value={commentTextDraft}
                        onChange={e => {
                          setCommentTextDraft(e.target.value)
                          growCommentWidthToFitText(c.id, e.currentTarget)
                        }}
                        onBlur={() => flushCommentTextFromDraft()}
                        onPointerDown={e => e.stopPropagation()}
                        placeholder="Type note..."
                      />
                    </>
                  ) : preview ? <div className="model-comment-box__excerpt">{preview}</div> : null}
                  {selected && (
                    <>
                      {resizeHandleDefs.map(handle => (
                        <div
                          key={handle.key}
                          className={`model-comment-resize-handle ${handle.className}${resizingCommentId === c.id ? " model-comment-resize-handle--active" : ""}`}
                          role="button"
                          tabIndex={-1}
                          aria-label={`Resize note from ${handle.key} corner`}
                          onPointerDown={e => {
                            if (!editor) return
                            e.preventDefault()
                            e.stopPropagation()
                            setResizingCommentId(c.id)

                            const startClientX = e.clientX
                            const startClientY = e.clientY
                            const base = { x: c.x, y: c.y, w: c.width, h: c.height }
                            const minSize = 64
                            let moved = false

                            const onMove = (ev: PointerEvent) => {
                              const t = editor.view.area.transform
                              const dx = (ev.clientX - startClientX) / t.k
                              const dy = (ev.clientY - startClientY) / t.k
                              let nextX = base.x
                              let nextY = base.y
                              let nextW = base.w
                              let nextH = base.h

                              if (handle.key.includes("e")) {
                                nextW = Math.max(minSize, base.w + dx)
                              }
                              if (handle.key.includes("s")) {
                                nextH = Math.max(minSize, base.h + dy)
                              }
                              if (handle.key.includes("w")) {
                                nextW = Math.max(minSize, base.w - dx)
                                nextX = base.x + (base.w - nextW)
                              }
                              if (handle.key.includes("n")) {
                                nextH = Math.max(minSize, base.h - dy)
                                nextY = base.y + (base.h - nextH)
                              }

                              if (
                                Math.abs(nextX - c.x) > 0.25 ||
                                Math.abs(nextY - c.y) > 0.25 ||
                                Math.abs(nextW - c.width) > 0.25 ||
                                Math.abs(nextH - c.height) > 0.25
                              ) {
                                moved = true
                              }
                              setCommentRectLocal(c.id, nextX, nextY, nextW, nextH)
                            }

                            const onUp = () => {
                              window.removeEventListener("pointermove", onMove)
                              window.removeEventListener("pointerup", onUp)
                              window.removeEventListener("pointercancel", onUp)
                              setResizingCommentId(null)
                              if (moved) writeModelRef.current?.({ movement: true })
                            }

                            window.addEventListener("pointermove", onMove)
                            window.addEventListener("pointerup", onUp)
                            window.addEventListener("pointercancel", onUp)
                          }}
                        />
                      ))}
                    </>
                  )}
                </div>
              )
            })}
            {commentDrawPreview && (
              <div
                className="model-comment-box model-comment-box--preview"
                style={{
                  position: "absolute",
                  left: commentDrawPreview.x,
                  top: commentDrawPreview.y,
                  width: commentDrawPreview.width,
                  height: commentDrawPreview.height,
                }}
              />
            )}
          </>,
          commentPortalHost
        )}
    </div>
  )
}

function updateForBackwardsCompatibility(initialModel: Data, components: any): Data {

  let model = initialModel;
  let nodeNames = Object.keys(model.nodes)
  let activeComponents = Array.from(components.keys())
  let inactiveComponents: number[] = []

  // Consolidate legacy Distance Map variants onto the single component name.
  for (let i = 0; i < nodeNames.length; i++) {
    const node = model.nodes[nodeNames[i]]
    if (!node?.name) continue
    if (node.name === "Distance map" || node.name === "Distance map (beta)") {
      node.name = "Distance Map"
      model.nodes[nodeNames[i]] = node
    }
  }

  // remove and log any components that have been deleted/updated
  for (let i = 0; i < nodeNames.length; i++) {
    let node = model.nodes[nodeNames[i]]
    if(!activeComponents.includes(node.name)) {
      inactiveComponents.push(node.id)
      delete model.nodes[nodeNames[i]]
      nodeNames[i] = "deleted"
    }
  }

  // remove any connections that are connected to deleted/updated components
  if(inactiveComponents.length > 0) {
    for (let i = 0; i < nodeNames.length; i++) {
      if(nodeNames[i] !== "deleted") {
        let node = model.nodes[nodeNames[i]]
        let inputs = Object.keys(node.inputs)
        let outputs = Object.keys(node.outputs)

        for (let j = 0; j < inputs.length; j++) {
          let input = node.inputs[inputs[j]]
          input.connections.forEach((connection: any, index: number) => {
            if(inactiveComponents.includes(connection.node)) {
              input.connections.splice(index, 1)
            }
          })
          model.nodes[nodeNames[i]].inputs[inputs[j]] = input
        }
        

        for (let j = 0; j < outputs.length; j++) {
          let output = node.outputs[outputs[j]]
          output.connections.forEach((connection: any, index: number) => {
            if(inactiveComponents.includes(connection.node)) {
              output.connections.splice(index, 1)
            }
          })
          model.nodes[nodeNames[i]].outputs[outputs[j]] = output
        }
        
        
      }
  
    }
  }

  return model
}
